import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  RefreshCw,
  FileText,
  Loader2,
  Eye,
  Edit3,
  CheckCircle,
  Zap,
  Wand2,
  Trash2,
  Plus,
  Check,
  X,
  MoreHorizontal,
  GripVertical,
  ChevronRight,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import {
  Textarea,
} from "../../../foundation/altzero_base/base/components/ui/textarea";
import {
  Label,
} from "../../../foundation/altzero_base/base/components/ui/label";
import { Progress } from "../../../foundation/altzero_base/base/components/ui/progress";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../foundation/altzero_base/base/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../foundation/altzero_base/base/components/ui/dropdown-menu";
import ScopingAILayout from "../components/ScopingAILayout";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { fetchDocumentFile } from "../services/documentService";
import PerfectMarkdownRenderer from "../components/common/PerfectMarkdownRenderer";
import PDFLikePreview, { PDFLikePreviewRef } from "../components/PDFLikePreview";

import YooptaMarkdownEditor from "../components/editor/YooptaMarkdownEditor";
import { convertSectionsToYoopta, convertYooptaToMarkdown, convertMarkdownToYoopta } from "../utils/yooptaConverter";

// Types for regeneration
interface RegenerationData {
  existingDocument: {
    id: string;
    title: string;
    content: string;
    template_id?: string;
    sections: Array<{
      id: string;
      title: string;
      content: string;
    }>;
  };
  newClientData: {
    id?: string;
    name: string;
    email: string;
    company: string;
    description: string;
    industry?: string;
  };
  newRequirements: {
    projectTitle: string;
    projectName?: string;
    description: string;
    projectDescription?: string;
    objectives: string[];
    timeline: string;
    budget: string;
    scope?: string;
  };
  selectedKnowledgeDocuments: string[];
  // Add missing properties for comprehensive regeneration
  aiPrompts?: {
    customPrompt?: {
      content: string;
    };
    content?: string;
    selectedPrompt?: {
      id?: string;
      name?: string;
      content: string;
      variables?: any[];
    };
    enhancedAiPrompts?: {
      customPrompt?: {
        content: string;
      };
    };
  };
  template?: {
    id: string;
    name: string;
    sections: any[];
  };
  documentRequirements?: Record<string, string>;
  fromProposalsPage?: boolean; // 🚨 NEW: Flag to indicate source
  regenerateOnly?: boolean; // 🚨 NEW: Flag to prevent auto-regeneration
  fromQuickProposal?: boolean; // 🚨 NEW: Flag to indicate Quick Proposal source
}

interface RegenerationProgress {
  isGenerating: boolean;
  progress: number;
  currentStep: string;
  message: string;
  completedSections: string[];
}

// Sortable Section Component
interface SortableSectionProps {
  section: any;
  selectedSectionId: string | null;
  editingTitles: Set<string>;
  titleInputs: Record<string, string>;
  onSectionClick: (sectionId: string) => void;
  onTitleChange: (sectionId: string, value: string) => void;
  onTitleSave: (sectionId: string) => void;
  onTitleCancel: (sectionId: string) => void;
  onEditClick: (sectionId: string) => void;
  onToggleTitleEditing: (sectionId: string) => void;
  onAddSectionAfter: (sectionId: string) => void;
  onSectionRegenerate: (sectionId: string, sectionTitle: string) => void;
  onDeleteSection: (sectionId: string) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  selectedSectionId,
  editingTitles,
  titleInputs,
  onSectionClick,
  onTitleChange,
  onTitleSave,
  onTitleCancel,
  onEditClick,
  onToggleTitleEditing,
  onAddSectionAfter,
  onSectionRegenerate,
  onDeleteSection,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 mb-2 border rounded-lg transition-all overflow-hidden ${
        selectedSectionId === section.id
          ? "bg-blue-50 border-blue-300 shadow-sm"
          : "bg-white hover:border-blue-200 hover:shadow-sm"
      } ${isDragging ? "shadow-lg" : ""}`}
    >
      <div className="flex items-start gap-2">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 mt-1 p-1 hover:bg-gray-100 rounded cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>

        {/* Section Content */}
        <div
          className="flex-1 cursor-pointer min-w-0 overflow-hidden"
          onClick={() => onSectionClick(section.id)}
        >
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-medium text-sm text-gray-900 truncate flex-1 min-w-0 pr-2 max-w-full overflow-hidden">
              {editingTitles.has(section.id) ? (
                <Input
                  value={titleInputs[section.id] || ""}
                  onChange={(e) => {
                    // Limit title length to prevent menu breaking
                    const value = e.target.value.substring(0, 100);
                    onTitleChange(section.id, value);
                  }}
                  onBlur={() => onTitleSave(section.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onTitleSave(section.id);
                    } else if (e.key === "Escape") {
                      onTitleCancel(section.id);
                    }
                  }}
                  className="w-full text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Section title (max 100 chars)"
                  maxLength={100}
                  autoFocus
                />
              ) : (
                (section.title || "Untitled").trim()
              )}
            </h4>
            <div className="flex items-center gap-1 flex-shrink-0">
              {section.edited && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✓
                </span>
              )}
              {section.regenerated && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  ↻
                </span>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 line-clamp-2 max-w-full overflow-hidden">
            {section.content ?
              section.content.trim().substring(0, 80) + (section.content.length > 80 ? '...' : '') :
              'No content'
            }
          </p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-400">
              {section.content?.length || 0} chars
            </span>
            <div className="flex items-center gap-1">
              {/* Primary Edit Button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick(section.id);
                }}
              >
                <Edit3 className="h-3 w-3 mr-1" />
                Edit
              </Button>

              {/* More Actions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleTitleEditing(section.id);
                    }}
                  >
                    <Edit3 className="h-3 w-3 mr-2" />
                    Edit Title
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddSectionAfter(section.id);
                    }}
                  >
                    <Plus className="h-3 w-3 mr-2" />
                    Add After
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onSectionRegenerate(section.id, section.title);
                    }}
                  >
                    <RefreshCw className="h-3 w-3 mr-2" />
                    Regenerate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSection(section.id);
                    }}
                    className="text-red-600"
                  >
                    <Trash2 className="h-3 w-3 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main document regeneration component (standalone implementation)
function DocumentRegenerationContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useUser();

  // Regeneration specific state
  const [regenerationData, setRegenerationData] =
    useState<RegenerationData | null>(null);
  const [regenerationProgress, setRegenerationProgress] =
    useState<RegenerationProgress>({
      isGenerating: false,
      progress: 0,
      currentStep: "",
      message: "",
      completedSections: [],
    });

  // 🚨 NEW: Add initialization state tracking
  const [initializationState, setInitializationState] = useState<{
    isInitializing: boolean;
    hasError: boolean;
    errorMessage: string;
    canSave: boolean;
  }>({
    isInitializing: true,
    hasError: false,
    errorMessage: "",
    canSave: false,
  });

  // 🚨 NEW: Knowledge Base + Custom Prompt state
  const [showKnowledgePromptDialog, setShowKnowledgePromptDialog] = useState(false);
  const [knowledgeCustomPrompt, setKnowledgeCustomPrompt] = useState("");
  // 🚨 UPDATED: Tab state management for three-tab system (edit, section-preview, document-preview)
  const [activeTab, setActiveTab] = useState<'edit' | 'section-preview' | 'document-preview'>('section-preview');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editableSections, setEditableSections] = useState<any[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null
  );

  // Backup and recovery state
  const [originalSections, setOriginalSections] = useState<any[]>([]);
  const [showRegenerationModal, setShowRegenerationModal] = useState(false);


  // 🚨 NEW: Prompt popup states
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [showSectionPromptDialog, setShowSectionPromptDialog] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [selectedSectionForRegeneration, setSelectedSectionForRegeneration] = useState<{id: string, title: string} | null>(null);
  const [isRegeneratingWithPrompt, setIsRegeneratingWithPrompt] = useState(false);





  // 🚨 NEW: Section regeneration mode selection
  const [showSectionModeDialog, setShowSectionModeDialog] = useState(false);
  const [isRegeneratingSection, setIsRegeneratingSection] = useState(false);
  



  // 🚨 NEW: Editor state for Yoopta integration
  const [isEditing, setIsEditing] = useState(false);
  const [editingContent, setEditingContent] = useState<Record<string, string>>({});
  // 🚨 NEW: Editable section titles state
  const [editingTitles, setEditingTitles] = useState<Set<string>>(new Set());
  const [titleInputs, setTitleInputs] = useState<{[sectionId: string]: string}>({});
  const [previewUpdateTrigger, setPreviewUpdateTrigger] = useState(0); // Force preview re-render
  const [editorKey, setEditorKey] = useState(0); // Force editor re-initialization when needed

  // 🚨 NEW: Project title editing state
  const [isEditingProjectTitle, setIsEditingProjectTitle] = useState(false);
  const [projectTitleInput, setProjectTitleInput] = useState("");

  // 🚨 CRITICAL: Use ref to track the most current editableSections state
  // This ensures handleSaveAsNewProposal always gets the latest content
  const editableSectionsRef = useRef<any[]>([]);
  const sectionsReceivedRef = useRef(false); // Track if sections were received during regeneration

  // 🚨 NEW: Ref for PDFLikePreview component to access scroll functionality
  const pdfPreviewRef = useRef<PDFLikePreviewRef>(null);

  // Update ref whenever editableSections changes
  useEffect(() => {
    editableSectionsRef.current = editableSections;
  }, [editableSections]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for section reordering
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setEditableSections((sections) => {
        const oldIndex = sections.findIndex((section) => section.id === active.id);
        const newIndex = sections.findIndex((section) => section.id === over.id);

        const reorderedSections = arrayMove(sections, oldIndex, newIndex);

        // Update the order property for each section and mark as edited
        const sectionsWithUpdatedOrder = reorderedSections.map((section, index) => ({
          ...section,
          order: index,
          edited: true, // Mark as edited when reordered
        }));

        // Also update the ref
        editableSectionsRef.current = sectionsWithUpdatedOrder;

        console.log('🔄 Sections reordered:', {
          from: oldIndex,
          to: newIndex,
          sections: sectionsWithUpdatedOrder.map(s => ({ id: s.id, title: s.title, order: s.order }))
        });

        return sectionsWithUpdatedOrder;
      });
    }
  }, []);

  // 🔧 FIXED: Derive selectedSection from selectedSectionId and editableSections
  const selectedSection = useMemo(() => {
    if (!selectedSectionId || !editableSections.length) return null;
    const section = editableSections.find(section => section.id === selectedSectionId) || null;

    // 🔍 DEBUG: Log selected section state for preview synchronization
    console.log('🔄 selectedSection updated:', {
      selectedSectionId,
      sectionFound: !!section,
      sectionTitle: section?.title,
      contentLength: section?.content?.length || 0,
      lastModified: section?.lastModified,
      edited: section?.edited,
      totalSections: editableSections.length
    });

    return section;
  }, [selectedSectionId, editableSections]);

  // 🎯 Keyboard shortcut for saving (Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        if (isEditing && selectedSection) {
          handleSaveDocument();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, selectedSection]);

  // Initialize regeneration data from sessionStorage and fetch real document content
  useEffect(() => {
    const initializeRegenerationData = async () => {
      try {
        console.log("🔄 Initializing document regeneration...");

        // Get data from sessionStorage
        const sessionData = sessionStorage.getItem("scopingai_proposal_data");
        console.log("🔍 INITIALIZATION DEBUG:", {
          sessionDataExists: !!sessionData,
          sessionDataLength: sessionData?.length || 0,
          currentUrl: window.location.href,
          currentPath: window.location.pathname
        });

        if (!sessionData) {
          console.error("❌ No session data found for regeneration");
          console.error("🔍 This means navigation from proposals page failed to set session data");
          toast({
            title: "Error",
            description:
              "No document data found. Please select a document first.",
            variant: "destructive",
          });

          // 🚨 FIXED: Check referrer to navigate back to correct page
          const referrer = document.referrer;
          if (referrer.includes('/proposals')) {
            console.log('🔄 Error case: Navigating back to proposals page');
            navigate("/tender/proposals");
          } else if (referrer.includes('/quick-proposal')) {
            console.log('🔄 Error case: Navigating back to quick proposal');
            navigate("/tender/quick-proposal");
          } else {
            console.log('🔄 Error case: Navigating back to documents page (default)');
            navigate("/tender/documents");
          }
          return;
        }

        const parsedData = JSON.parse(sessionData);
        console.log("📄 Session data loaded successfully:", {
          hasDocument: !!parsedData.document,
          hasSelectedDocument: !!parsedData.selectedDocument,
          fromProposalsPage: parsedData.fromProposalsPage,
          documentId: parsedData.document?.id || parsedData.selectedDocument?.id,
          allKeys: Object.keys(parsedData)
        });

        // 🚨 NEW: Handle new proposal from quick proposal (no existing document)
        if (parsedData.fromQuickProposal || parsedData.isNewProposal) {
          console.log("🚨 NEW PROPOSAL FLOW: Creating new proposal from quick proposal data");
          
          // Create a mock document structure for new proposals
          const mockDocument = {
            id: `new-proposal-${Date.now()}`,
            title: parsedData.project?.title || "New Proposal",
            content: "",
            template_id: null,
            sections: [],
          };

          // Extract regeneration data for new proposal
          const regenerationInfo: RegenerationData = {
            existingDocument: mockDocument,
            newClientData: {
              id: parsedData.client?.id || null,
              name: parsedData.client?.name || "",
              email: parsedData.client?.email || "",
              company: parsedData.client?.company || "",
              description: parsedData.client?.description || "",
            },
            newRequirements: {
              projectTitle: parsedData.project?.title || "",
              description: parsedData.project?.description || "",
              objectives: parsedData.project?.objectives || [],
              timeline: parsedData.project?.timeline || "",
              budget: parsedData.project?.budget || "",
            },
            selectedKnowledgeDocuments: parsedData.selectedKnowledgeDocuments || [],
            documentRequirements: parsedData.documentRequirements || {},
            aiPrompts: parsedData.aiPrompts || {},
            fromProposalsPage: false,
            fromQuickProposal: parsedData.fromQuickProposal || false,
            regenerateOnly: parsedData.regenerateOnly || false,
          };

          console.log("✅ New proposal regeneration data prepared:", regenerationInfo);
          console.log("🔍 Quick Proposal flag check:", {
            fromQuickProposal: regenerationInfo.fromQuickProposal,
            parsedDataFromQuickProposal: parsedData.fromQuickProposal,
            isNewProposal: parsedData.isNewProposal
          });
          setRegenerationData(regenerationInfo);

          // 🚨 CRITICAL FIX: Set initialization state to allow saving for quick proposal
          setInitializationState({
            isInitializing: false,
            hasError: false,
            errorMessage: "",
            canSave: true,
          });

          // Create single default section for new proposal - user creates sections from scratch
          const defaultSections = [
            {
              id: "section-1",
              title: "New Section".trim(),
              content: "",
              level: 1,
              order: 0,
            }
          ];

          // Convert default sections to Yoopta format
          const sectionsWithYoopta = convertSectionsToYoopta(defaultSections);
          setEditableSections(sectionsWithYoopta);
          setOriginalSections([...defaultSections]); // Backup original

          console.log("✅ New proposal initialized with default sections and save enabled:", defaultSections.length);
          setIsLoading(false);
          return;
        }

        // Check for updated requirements from URL params or state
        const urlParams = new URLSearchParams(location.search);
        const updatedRequirements = {
          projectTitle:
            urlParams.get("projectTitle") || parsedData.project?.title || "",
          description:
            urlParams.get("description") ||
            parsedData.project?.description ||
            "",
          objectives: parsedData.project?.objectives || [],
          timeline:
            urlParams.get("timeline") || parsedData.project?.timeline || "",
          budget: urlParams.get("budget") || parsedData.project?.budget || "",
        };

        console.log("📋 Updated requirements:", updatedRequirements);

        // 🚨 FIX: Handle document ID resolution for both flows (proposals page + document generation)
        let documentId = null;

        // Priority 1: selectedDocument (from document generation flow)
        if (parsedData.selectedDocument?.id) {
          documentId = parsedData.selectedDocument.id;
          console.log("✅ Found selectedDocument ID:", documentId);
        }
        // Priority 2: document (from proposals page)
        else if (parsedData.document?.id) {
          documentId = parsedData.document.id;
          console.log("✅ Found document ID:", documentId);
        }
        // Priority 3: referenceDocument (legacy)
        else if (parsedData.referenceDocument?.id) {
          documentId = parsedData.referenceDocument.id;
          console.log("✅ Found referenceDocument ID:", documentId);
        }

        if (!documentId) {
          console.error("❌ No document ID found in session data");
          console.error("📋 Available keys:", Object.keys(parsedData));
          console.error("📋 Session data:", parsedData);
          throw new Error("No document ID found in session data");
        }

        console.log("🔍 Using document ID:", documentId);
        console.log("🔍 From proposals page?", parsedData.fromProposalsPage);

        console.log("🔍 Fetching document from database:", documentId);

        // Fetch the full document record from database to get proposalFilePath
        const { data: documentRecord, error: dbError } = await supabase
          .from("scopingai_documents")
          .select("*")
          .eq("id", documentId)
          .single();

        if (dbError || !documentRecord) {
          throw new Error(`Failed to fetch document: ${dbError?.message}`);
        }

        console.log("📄 Document record fetched:", documentRecord);

        // Fetch the actual markdown content from storage bucket
        let markdownContent = "";
        let parsedSections: any[] = [];

        if (documentRecord.proposalFilePath) {
          console.log(
            "📥 Fetching markdown content from bucket:",
            documentRecord.proposalFilePath
          );

          try {
            const fileContent = await fetchDocumentFile(
              documentRecord.proposalFilePath
            );

            if (typeof fileContent === "string") {
              // It's markdown content
              markdownContent = fileContent;
              const rawSections = parseMarkdownSections(markdownContent);
              parsedSections = rawSections.map((section: any, index: number) => ({
                id: section.id || `section-${index + 1}`,
                title: section.title || `Section ${index + 1}`,
                content: section.content || "",
                level: 1,
                order: index,
                edited: !!(section.content && section.content.trim()), // Mark as edited if has content
              }));
              console.log("📋 Parsed section titles from markdown:", parsedSections.map(s => s.title));
            }

            console.log("✅ Document content loaded:", {
              contentLength: markdownContent.length,
              sectionsCount: parsedSections.length,
            });
          } catch (fileError) {
            console.error(
              "❌ Failed to fetch file content - FILE-BASED SYSTEM REQUIRES VALID FILE:",
              fileError
            );
            throw new Error(`Failed to load document content from file: ${documentRecord.proposalFilePath}. This is a file-based system and requires a valid markdown file.`);
          }
        } else {
          console.error("❌ No proposalFilePath found - FILE-BASED SYSTEM REQUIRES FILE PATH");
          throw new Error("No proposalFilePath found. This is a file-based system and requires a valid file path to load content.");
        }

        // Fetch original generation metadata from database
        console.log("📊 Fetching original generation metadata...");
        let originalGenerationData = null;

        try {
          // Try to get original generation metadata from document record
          if (documentRecord.metadata && documentRecord.metadata.generationRequest) {
            originalGenerationData = documentRecord.metadata.generationRequest;
            console.log("✅ Found original generation metadata in document record");
          } else {
            // Fallback: Try to get from session data if available
            originalGenerationData = parsedData.originalGenerationData || null;
            console.log("⚠️ No generation metadata in document, using session fallback");
          }
        } catch (metadataError) {
          console.warn("⚠️ Failed to retrieve original generation metadata:", metadataError);
        }

        // Extract regeneration data with original generation context
        const regenerationInfo: RegenerationData = {
          existingDocument: {
            id: documentRecord.id,
            title: documentRecord.title,
            content: markdownContent,
            template_id: documentRecord.template_id,
            sections: parsedSections,
          },
          newClientData: {
            id: parsedData.client?.id || parsedData.selectedClient?.id || null, // 🚨 CRITICAL: Add client ID
            name: parsedData.client?.name || "",
            email: parsedData.client?.email || "",
            company: parsedData.client?.company || "",
            description: parsedData.client?.description || "",
          },
          newRequirements: updatedRequirements,
          selectedKnowledgeDocuments: parsedData.selectedKnowledgeDocuments || [],

          // Add original generation data (this is the key fix!)
          aiPrompts: originalGenerationData?.aiPrompts || parsedData.aiPrompts || {
            customPrompt: {
              content: "Regenerate this proposal with new client information and requirements while maintaining the original structure and professional quality. Ensure all sections are comprehensive and tailored to the new client's needs."
            }
          },
          template: originalGenerationData?.template || parsedData.template || {
            id: documentRecord.template_id || "existing_document",
            name: documentRecord.title || "Existing Document Template",
            sections: parsedSections.map(section => ({
              title: section.title,
              description: `Regenerate ${section.title} section`,
              required: true
            }))
          },
          documentRequirements: originalGenerationData?.requirements?.documentRequirements ||
                               parsedData.documentRequirements ||
                               {},
          fromProposalsPage: parsedData.fromProposalsPage || false, // 🚨 NEW: Pass through the flag
          fromQuickProposal: parsedData.fromQuickProposal || false, // 🚨 NEW: Pass through the Quick Proposal flag
          regenerateOnly: parsedData.regenerateOnly || false,
        };

        setRegenerationData(regenerationInfo);

        console.log("🚨 CRITICAL DEBUG - setRegenerationData called with:", {
          regenerationInfoExists: !!regenerationInfo,
          fromProposalsPage: regenerationInfo.fromProposalsPage,
          fromQuickProposal: regenerationInfo.fromQuickProposal,
          existingDocumentId: regenerationInfo.existingDocument?.id,
          existingDocumentTitle: regenerationInfo.existingDocument?.title
        });

        // 🚨 FIXED: Update initialization state on success
        setInitializationState({
          isInitializing: false,
          hasError: false,
          errorMessage: "",
          canSave: true,
        });

        console.log("🚨 CRITICAL DEBUG - Initialization completed successfully");

        // Check session storage for regenerated content (only during initialization)
        let sectionsToUse = parsedSections;

        try {
          const sessionData = sessionStorage.getItem("scopingai_proposal_data");
          if (sessionData) {
            const parsedSessionData = JSON.parse(sessionData);

            // Priority 1: Check for regeneratedSections (new structure)
            if (parsedSessionData.isRegenerated && parsedSessionData.regeneratedSections && parsedSessionData.regeneratedSections.length > 0) {
              console.log("Loading regenerated content:", parsedSessionData.regeneratedSections.length, "sections");
              sectionsToUse = parsedSessionData.regeneratedSections;
            }
            // Priority 2: Check regeneratedContent (old structure)
            else if (parsedSessionData.regeneratedContent && parsedSessionData.regeneratedContent.sections) {
              sectionsToUse = parsedSessionData.regeneratedContent.sections;
            }
            // Priority 3: Check proposal with regenerated flag
            else if (parsedSessionData.proposal && parsedSessionData.proposal.sections && parsedSessionData.proposal.regenerated) {
              sectionsToUse = parsedSessionData.proposal.sections;
            }
            // Priority 4: Check if marked as regenerated
            else if (parsedSessionData.isRegenerated && parsedSessionData.proposal && parsedSessionData.proposal.sections) {
              sectionsToUse = parsedSessionData.proposal.sections;
            }
          }
        } catch (error) {
          console.error("Failed to load regenerated content:", error);
        }

        // Convert sections to Yoopta format and set initial state
        const sectionsWithYoopta = convertSectionsToYoopta(sectionsToUse);
        setEditableSections(sectionsWithYoopta);
        setOriginalSections([...parsedSections]); // Always backup original
        
        console.log("Loaded", sectionsToUse.length, "sections for editing");

        // Auto-select first section by default
        if (parsedSections.length > 0) {
          setSelectedSectionId(parsedSections[0].id);
        }

        console.log("📋 Initial sections set in UI:", parsedSections.map(s => s.title));

        setIsLoading(false);

        console.log("✅ Regeneration data initialized with real content:", {
          documentId: regenerationInfo.existingDocument.id,
          title: regenerationInfo.existingDocument.title,
          sectionsCount: parsedSections.length,
          contentLength: markdownContent.length,
        });

        // Check if auto-regeneration is requested
        const autoTriggerParams = new URLSearchParams(location.search);
        const shouldAutoRegenerate = autoTriggerParams.get('autoRegenerate') === 'true';
        
        // 🚨 NEW: Check for regenerateOnly flag to prevent auto-regeneration
        const shouldSkipAutoRegeneration = parsedData.regenerateOnly === true;

        if (shouldAutoRegenerate && !shouldSkipAutoRegeneration) {
          console.log("🚀 Auto-triggering full regeneration...");
          // Store the regeneration data and trigger auto-regeneration
          // We'll use a separate useEffect to handle this after component is fully initialized
          sessionStorage.setItem('autoTriggerRegeneration', 'true');
        } else if (shouldSkipAutoRegeneration) {
          console.log("🚫 Skipping auto-regeneration due to regenerateOnly flag");
        }
      } catch (error) {
        console.error("❌ Failed to initialize regeneration data:", error);
        console.error("🔍 Error details:", {
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          sessionDataExists: !!sessionStorage.getItem("scopingai_proposal_data")
        });

        // 🚨 FIXED: Set error state instead of immediately navigating away
        setInitializationState({
          isInitializing: false,
          hasError: true,
          errorMessage: error instanceof Error ? error.message : "Failed to load document for regeneration",
          canSave: false,
        });

        toast({
          title: "Error",
          description: "Failed to load document for regeneration. You can try to reload the page or go back to documents.",
          variant: "destructive",
        });
        setIsLoading(false);

        // 🚨 FIXED: Don't auto-navigate, let user decide what to do
        // navigate("/tender/documents");

        // 🚨 NEW: Try to create fallback regeneration data if we have session data
        try {
          const sessionData = sessionStorage.getItem("scopingai_proposal_data");
          if (sessionData) {
            const parsedData = JSON.parse(sessionData);
            if (parsedData.proposal?.sections && parsedData.proposal.sections.length > 0) {
              console.log("🔄 Creating fallback regeneration data from session storage");

              const fallbackRegenerationData: RegenerationData = {
                existingDocument: {
                  id: parsedData.documentId || `fallback-${Date.now()}`,
                  title: parsedData.proposal.title || parsedData.documentData?.title || "Document",
                  content: "", // Will be populated from sections
                  template_id: null,
                  sections: parsedData.proposal.sections,
                },
                newClientData: {
                  id: parsedData.client?.id || null,
                  name: parsedData.client?.name || parsedData.proposal?.clientName || "Client",
                  email: parsedData.client?.email || "",
                  company: parsedData.client?.company || parsedData.proposal?.clientName || "Company",
                  description: parsedData.client?.description || "Client description",
                  industry: parsedData.client?.industry || "Industry",
                },
                newRequirements: {
                  projectTitle: parsedData.proposal?.title || "Project",
                  description: parsedData.requirements || "Project requirements",
                  objectives: parsedData.project?.objectives || [],
                  timeline: parsedData.project?.timeline || "Timeline TBD",
                  budget: parsedData.project?.budget || "Budget TBD",
                  scope: parsedData.requirements || "Project requirements",
                },
                selectedKnowledgeDocuments: [],
                documentRequirements: {},
                aiPrompts: null,
                fromProposalsPage: parsedData.fromProposalsPage || false,
                fromQuickProposal: parsedData.fromQuickProposal || false,
                regenerateOnly: parsedData.regenerateOnly || false,
              };

              setRegenerationData(fallbackRegenerationData);

              // Convert sections and set them up
              const sectionsWithYoopta = convertSectionsToYoopta(parsedData.proposal.sections);
              setEditableSections(sectionsWithYoopta);

              // Update state to allow saving with fallback data
              setInitializationState({
                isInitializing: false,
                hasError: false,
                errorMessage: "",
                canSave: true,
              });

              console.log("✅ Fallback regeneration data created successfully");

              toast({
                title: "Document Loaded",
                description: "Document loaded with fallback data. You can edit and save your changes.",
                variant: "default",
              });
            }
          }
        } catch (fallbackError) {
          console.error("❌ Failed to create fallback data:", fallbackError);
        }
      }
    };

    initializeRegenerationData();
  }, []);

  // 🚨 FIXED: Use standardized H2-only section parsing (matches server-side logic)
  const parseMarkdownSections = (content: string) => {
    const lines = content.split("\n");
    const sections = [];
    let currentSection = null;
    let sectionContent = "";

    console.log("📋 Frontend: Parsing markdown with H2-only standard...");

    for (const line of lines) {
      // Skip H1 (document title)
      if (line.match(/^#\s+(.+)$/)) {
        continue;
      }

      // Check for H2 headers ONLY (our standard)
      const headerMatch = line.match(/^##\s+(.+)$/);
      if (headerMatch) {
        // Save previous section if exists
        if (currentSection) {
          sections.push({
            id: `section-${sections.length + 1}`,
            title: currentSection,
            content: sectionContent.trim(),
          });
        }

        // Start new section
        const title = headerMatch[1].trim();
        currentSection = title;
        sectionContent = "";
        console.log(`📋 Found H2 section: "${title}"`);
      } else if (currentSection) {
        sectionContent += line + "\n";
      }
    }

    // Add final section if exists
    if (currentSection) {
      sections.push({
        id: `section-${sections.length + 1}`,
        title: currentSection,
        content: sectionContent.trim(),
      });
    }

    console.log(`✅ Parsed ${sections.length} sections using H2 standard:`, sections.map(s => s.title));

    // If no sections found, create a single section with all content
    return sections.length > 0
      ? sections
      : [
          {
            id: "section-1",
            title: "Document Content",
            content: content,
          },
        ];
  };

  // Handle full document regeneration with REAL API call
  const handleFullRegeneration = async () => {
    if (!regenerationData) {
      console.error("🚨 CRITICAL: No regenerationData available for handleFullRegeneration");
      return;
    }

    console.log("🚨 STARTING handleFullRegeneration - Current editableSections:", editableSections.length);
    console.log("🚨 RegenerationData:", regenerationData.existingDocument.title);

    // Show beautiful loading modal
    setShowRegenerationModal(true);

    setRegenerationProgress({
      isGenerating: true,
      progress: 0,
      currentStep: "Starting full document regeneration...",
      message: "Preparing to regenerate all sections with new requirements",
      completedSections: [],
    });

    try {
      console.log(
        "🔄 Starting REAL full document regeneration:",
        regenerationData
      );

      // Get proper client ID from session data
      const sessionData = sessionStorage.getItem("scopingai_proposal_data");
      const parsedSessionData = sessionData ? JSON.parse(sessionData) : {};

      console.log("📊 Session data analysis:", {
        hasSessionData: !!sessionData,
        sessionDataKeys: Object.keys(parsedSessionData),
        clientData: parsedSessionData.client,
        aiPrompts: parsedSessionData.aiPrompts,
        documentRequirements: parsedSessionData.documentRequirements,
        selectedKnowledgeDocuments: parsedSessionData.selectedKnowledgeDocuments
      });

      // Extract proper AI prompt from selected prompt or enhanced prompts
      // Check all possible locations where the AI prompt could be stored
      const selectedAiPrompt =
        // First check if selectedPrompt has content directly
        regenerationData.aiPrompts?.selectedPrompt?.content ||
        // Then check enhancedAiPrompts structure
        regenerationData.aiPrompts?.enhancedAiPrompts?.customPrompt?.content ||
        // Check if selectedPrompt is the object itself (not nested)
        (typeof regenerationData.aiPrompts?.selectedPrompt === 'object' &&
         regenerationData.aiPrompts?.selectedPrompt?.content) ||
        // Check customPrompt
        regenerationData.aiPrompts?.customPrompt?.content ||
        // Check direct content
        regenerationData.aiPrompts?.content ||
        // Check if parsedSessionData has the AI prompt data
        parsedSessionData.aiPrompts?.selectedPrompt?.content ||
        parsedSessionData.aiPrompts?.enhancedAiPrompts?.customPrompt?.content ||
        parsedSessionData.aiPrompts?.customPrompt?.content;

      console.log("🤖 AI Prompt resolution debug:", {
        regenerationDataAiPrompts: regenerationData.aiPrompts,
        selectedPrompt: regenerationData.aiPrompts?.selectedPrompt,
        enhancedPrompts: regenerationData.aiPrompts?.enhancedAiPrompts,
        customPrompt: regenerationData.aiPrompts?.customPrompt,
        sessionAiPrompts: parsedSessionData.aiPrompts,
        finalPrompt: selectedAiPrompt,
        promptLength: selectedAiPrompt?.length || 0
      });

      // Prepare CLEAN payload for regeneration API (EXACT structure as specified)
      const regenerationPayload = {
        clientDetails: {
          // 🚨 FIX: Proper clientId handling like normal generation flow
          clientId: (() => {
            const sessionClientId = parsedSessionData.selectedClient?.id || parsedSessionData.client?.id;
            const regenerationClientId = regenerationData.newClientData.id;
            const finalClientId = sessionClientId || regenerationClientId || null;

            // Validate clientId - don't use user ID as client ID
            if (finalClientId === user?.id) {
              console.warn("🚨 WARNING: Client ID matches User ID in payload - setting to null");
              return null;
            }

            console.log("🔍 Payload Client ID resolution:", {
              sessionClientId,
              regenerationClientId,
              userId: user?.id,
              finalClientId,
              isValid: finalClientId !== user?.id
            });

            return finalClientId;
          })(),
          name: regenerationData.newClientData.name,
          email: regenerationData.newClientData.email,
          company: regenerationData.newClientData.company,
          industry: regenerationData.newClientData.industry,
          description: regenerationData.newClientData.description
        },
        projectDetails: {
          name: regenerationData.newRequirements.projectTitle || regenerationData.newRequirements.projectName,
          title: regenerationData.newRequirements.projectTitle || regenerationData.newRequirements.projectName,
          description: regenerationData.newRequirements.description || regenerationData.newRequirements.projectDescription,
          objectives: regenerationData.newRequirements.objectives,
          timeline: regenerationData.newRequirements.timeline,
          budget: regenerationData.newRequirements.budget,
          scope: regenerationData.newRequirements.scope
        },
        requirements: regenerationData.selectedKnowledgeDocuments && regenerationData.selectedKnowledgeDocuments.length > 0
          ? {
              type: "knowledge_base",
              documents: regenerationData.selectedKnowledgeDocuments.map(docId => ({
                documentId: docId,
                extractPrompt: regenerationData.documentRequirements?.[docId] || "Get Key information from this document based on the selected template sections."
              }))
            }
          : {
              type: "manual",
              text: regenerationData.newRequirements.description || regenerationData.newRequirements.projectDescription || ""
            },
        templateId: regenerationData.existingDocument.id,
        aiPrompt: selectedAiPrompt || "Generate professional proposal content with new client and project requirements while maintaining structure and quality. IMPORTANT: Do not generate any placeholder images, dummy images, or image references like 'Image not available', 'Mission Statement Image', 'Company Logo', etc. Only preserve existing real images if they exist."
      };

      console.log("📤 Clean regeneration payload (exact structure):", {
        hasClientDetails: !!regenerationPayload.clientDetails,
        hasProjectDetails: !!regenerationPayload.projectDetails,
        hasRequirements: !!regenerationPayload.requirements,
        hasTemplateId: !!regenerationPayload.templateId,
        hasAiPrompt: !!regenerationPayload.aiPrompt,
        requirementsType: regenerationPayload.requirements.type,
        clientName: regenerationPayload.clientDetails?.name,
        clientId: regenerationPayload.clientDetails?.clientId,
        projectName: regenerationPayload.projectDetails?.name,
        aiPromptLength: regenerationPayload.aiPrompt?.length || 0,
        aiPromptPreview: regenerationPayload.aiPrompt?.substring(0, 100) + "...",
        documentsCount: regenerationPayload.requirements.type === "knowledge_base" ? regenerationPayload.requirements.documents?.length : 0,
        extractPrompts: regenerationPayload.requirements.type === "knowledge_base" ?
          regenerationPayload.requirements.documents?.map(doc => ({
            docId: doc.documentId,
            prompt: doc.extractPrompt?.substring(0, 50) + "..."
          })) : []
      });

      // Also log the full payload for debugging (can be removed later)
      console.log("📋 FULL REGENERATION PAYLOAD:", JSON.stringify(regenerationPayload, null, 2));

      // Call REAL regeneration API with Server-Sent Events (COMPLETE payload like normal generation)
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tender/regenerate/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "scopingai",
            "x-user-id": user?.id || "",
          },
          body: JSON.stringify(regenerationPayload), // Send complete payload
        }
      );

      if (!response.ok) {
        // 🚨 ENHANCED ERROR HANDLING: Get detailed error information
        let errorDetails = "";
        try {
          const errorResponse = await response.text();
          errorDetails = errorResponse;
          console.error("❌ API Error Response:", errorResponse);
        } catch (parseError) {
          console.error("❌ Could not parse error response:", parseError);
        }

        console.error("❌ Section regeneration API call failed:", {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          headers: Object.fromEntries(response.headers.entries()),
          errorDetails: errorDetails
        });

        throw new Error(
          `API call failed: ${response.status} ${response.statusText}${errorDetails ? ` - ${errorDetails}` : ''}`
        );
      }

      // Handle Server-Sent Events for real-time progress
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let sectionsReceived = false; // 🚨 FIX: Track if we received sections
      sectionsReceivedRef.current = false; // Reset the ref
      let incompleteBuffer = ''; // Buffer for incomplete JSON chunks

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          
          // 🚨 CRITICAL: Handle chunked data properly
          const fullChunk = incompleteBuffer + chunk;
          incompleteBuffer = ''; // Reset buffer
          
          const lines = fullChunk.split("\n");
          
          // If the last line doesn't end with a complete event, save it for next chunk
          if (lines.length > 0 && !lines[lines.length - 1].endsWith('}') && lines[lines.length - 1].startsWith('data: ')) {
            incompleteBuffer = lines.pop() || '';
            console.log("🔧 INCOMPLETE LINE DETECTED - BUFFERING FOR NEXT CHUNK:", incompleteBuffer.length);
          }

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                let jsonString = line.slice(6);
                
                // 🚨 CRITICAL FIX: Handle potential JSON truncation/corruption
                console.log("🔧 RAW JSON STRING LENGTH:", jsonString.length);
                console.log("🔧 RAW JSON STRING (first 200 chars):", jsonString.substring(0, 200));
                console.log("🔧 RAW JSON STRING (last 200 chars):", jsonString.substring(jsonString.length - 200));
                
                // Try to repair common JSON issues
                jsonString = jsonString.trim();
                
                // Check if JSON string is complete (starts with { and ends with })
                if (!jsonString.startsWith('{') || !jsonString.endsWith('}')) {
                  console.warn("⚠️ INCOMPLETE JSON DETECTED:", { 
                    startsWithBrace: jsonString.startsWith('{'),
                    endsWithBrace: jsonString.endsWith('}'),
                    length: jsonString.length
                  });
                  
                  // If it doesn't end with }, try to find where it should end
                  if (jsonString.startsWith('{') && !jsonString.endsWith('}')) {
                    // Count braces to find proper ending
                    let braceCount = 0;
                    let properEnd = -1;
                    for (let i = 0; i < jsonString.length; i++) {
                      if (jsonString[i] === '{') braceCount++;
                      if (jsonString[i] === '}') braceCount--;
                      if (braceCount === 0) {
                        properEnd = i;
                        break;
                      }
                    }
                    
                    if (properEnd > 0) {
                      console.log("🔧 ATTEMPTING JSON REPAIR - truncating at position:", properEnd + 1);
                      jsonString = jsonString.substring(0, properEnd + 1);
                    }
                  }
                }
                
                const data = JSON.parse(jsonString);
                console.log("📡 SSE Data received:", data);
                console.log("📡 SSE Data keys:", Object.keys(data));
                console.log("📡 SSE Data status:", data.status);
                console.log("📡 Has sections?", !!data.sections);
                console.log("📡 Has result?", !!data.result);
                console.log("📡 Has result.sections?", !!(data.result && data.result.sections));

                // 🚨 LOG EXACTLY WHAT WE'RE RECEIVING
                if (data.sections) {
                  console.log("🚨 FRONTEND: Received sections directly:", data.sections.length);
                }
                if (data.result && data.result.sections) {
                  console.log("🚨 FRONTEND: Received sections in result:", data.result.sections.length);
                }

                if (data.progress !== undefined) {
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    progress: data.progress,
                    message: data.message || prev.message,
                    currentStep: data.step || prev.currentStep,
                    completedSections: data.completedSections || prev.completedSections,
                  }));
                }

                // 🚨 COMPREHENSIVE: Check ALL possible ways sections can be sent from server
                let sectionsToProcess = null;
                
                // 🚨 CRITICAL DEBUG: Show exactly what we're receiving
                console.log("🚨 RAW SSE DATA:", data);
                console.log("🚨 DATA TYPE:", typeof data);
                console.log("🚨 DATA KEYS:", data ? Object.keys(data) : 'NO DATA');
                if (data && data.sections) {
                  console.log("🚨 SECTIONS TYPE:", typeof data.sections);
                  console.log("🚨 SECTIONS IS ARRAY:", Array.isArray(data.sections));
                  console.log("🚨 SECTIONS LENGTH:", data.sections.length);
                }
                if (data && data.result) {
                  console.log("🚨 RESULT KEYS:", Object.keys(data.result));
                  if (data.result.sections) {
                    console.log("🚨 RESULT.SECTIONS TYPE:", typeof data.result.sections);
                    console.log("🚨 RESULT.SECTIONS IS ARRAY:", Array.isArray(data.result.sections));
                    console.log("🚨 RESULT.SECTIONS LENGTH:", data.result.sections.length);
                  }
                }

                console.log("🚨 NORMAL REGENERATION SSE DATA RECEIVED:", {
                  hasData: !!data,
                  dataKeys: Object.keys(data),
                  hasSections: !!data.sections,
                  hasResult: !!data.result,
                  status: data.status,
                  progress: data.progress,
                  event: data.event || "unknown"
                });

                // 🚨 CRITICAL: Log the complete data structure for debugging
                console.log("🚨 COMPLETE SSE DATA STRUCTURE:", JSON.stringify(data, null, 2));
                
                // 🚨 EXTRA CRITICAL: Log sections content specifically
                if (data.sections && Array.isArray(data.sections)) {
                  console.log("🚨 RAW SECTIONS FROM SERVER:", data.sections.length);
                  data.sections.forEach((section: any, index: number) => {
                    console.log(`🚨 RAW SECTION ${index}:`, {
                      title: section.title,
                      hasContent: !!section.content,
                      contentLength: section.content?.length || 0,
                      contentPreview: section.content?.substring(0, 200) || 'NO CONTENT',
                      allKeys: Object.keys(section)
                    });
                  });
                }
                if (data.result && data.result.sections && Array.isArray(data.result.sections)) {
                  console.log("🚨 RAW SECTIONS FROM SERVER (in result):", data.result.sections.length);
                  data.result.sections.forEach((section: any, index: number) => {
                    console.log(`🚨 RAW SECTION ${index} (result):`, {
                      title: section.title,
                      hasContent: !!section.content,
                      contentLength: section.content?.length || 0,
                      contentPreview: section.content?.substring(0, 200) || 'NO CONTENT',
                      allKeys: Object.keys(section)
                    });
                  });
                }

                // Method 1: Direct sections array (most common)
                if (data.sections && Array.isArray(data.sections) && data.sections.length > 0) {
                  sectionsToProcess = data.sections;
                  console.log("🚨 NORMAL REGEN: ✅ Found sections in data.sections:", sectionsToProcess.length);
                }
                // Method 2: In result object (common for completed events)
                else if (data.result && data.result.sections && Array.isArray(data.result.sections) && data.result.sections.length > 0) {
                  sectionsToProcess = data.result.sections;
                  console.log("🚨 NORMAL REGEN: ✅ Found sections in data.result.sections:", sectionsToProcess.length);
                  console.log("🚨 First result section:", data.result.sections[0]?.title);
                }
                // Method 3: Completion event with sections at root level
                else if (data.status === "completed" && data.sections && Array.isArray(data.sections)) {
                  sectionsToProcess = data.sections;
                  console.log("🚨 NORMAL REGEN: ✅ Found sections in completed event (root):", sectionsToProcess.length);
                }
                // Method 4: Completion event with sections in result
                else if (data.status === "completed" && data.result && data.result.sections && Array.isArray(data.result.sections)) {
                  sectionsToProcess = data.result.sections;
                  console.log("🚨 NORMAL REGEN: ✅ Found sections in completed event (result):", sectionsToProcess.length);
                }
                // Method 5: Nested result structure
                else if (data.result && data.result.result && data.result.result.sections && Array.isArray(data.result.result.sections)) {
                  sectionsToProcess = data.result.result.sections;
                  console.log("🚨 NORMAL REGEN: ✅ Found sections in nested result:", sectionsToProcess.length);
                }
                // Method 6: Check for any other possible locations
                else if (data.generatedSections && Array.isArray(data.generatedSections)) {
                  sectionsToProcess = data.generatedSections;
                  console.log("🚨 NORMAL REGEN: ✅ Found sections in data.generatedSections:", sectionsToProcess.length);
                }
                // Method 7: Check for workflow result structure
                else if (data.workflowResult && data.workflowResult.sections && Array.isArray(data.workflowResult.sections)) {
                  sectionsToProcess = data.workflowResult.sections;
                  console.log("🚨 NORMAL REGEN: ✅ Found sections in data.workflowResult.sections:", sectionsToProcess.length);
                }
                // Method 8: AGGRESSIVE FALLBACK - Check ANY property that contains sections
                else {
                  console.log("🚨 NORMAL REGEN: ❌ NO SECTIONS FOUND IN STANDARD LOCATIONS - SCANNING ALL PROPERTIES");
                  for (const [key, value] of Object.entries(data)) {
                    if (value && Array.isArray(value) && value.length > 0) {
                      // Check if this array contains section-like objects
                      const firstItem = value[0];
                      if (firstItem && typeof firstItem === 'object' && (firstItem.title || firstItem.content || firstItem.id)) {
                        console.log(`🚨 NORMAL REGEN: ✅ FOUND SECTIONS IN data.${key}:`, value.length);
                        sectionsToProcess = value;
                        break;
                      }
                    }
                    // Also check nested objects
                    if (value && typeof value === 'object' && !Array.isArray(value)) {
                      for (const [nestedKey, nestedValue] of Object.entries(value)) {
                        if (nestedValue && Array.isArray(nestedValue) && nestedValue.length > 0) {
                          const firstNestedItem = nestedValue[0];
                          if (firstNestedItem && typeof firstNestedItem === 'object' && (firstNestedItem.title || firstNestedItem.content || firstNestedItem.id)) {
                            console.log(`🚨 NORMAL REGEN: ✅ FOUND SECTIONS IN data.${key}.${nestedKey}:`, nestedValue.length);
                            sectionsToProcess = nestedValue;
                            break;
                          }
                        }
                      }
                      if (sectionsToProcess) break;
                    }
                  }
                }

                if (!sectionsToProcess) {
                  console.log("🚨 NORMAL REGEN: ❌ NO SECTIONS FOUND IN THIS SSE EVENT");
                  console.log("🚨 Available data keys:", Object.keys(data));
                  // Log all possible nested structures
                  if (data.result) {
                    console.log("🚨 data.result keys:", Object.keys(data.result));
                    if (data.result.result) {
                      console.log("🚨 data.result.result keys:", Object.keys(data.result.result));
                    }
                  }
                } else {
                  console.log("🚨 NORMAL REGEN: ✅ SECTIONS FOUND - First section preview:", {
                    title: sectionsToProcess[0]?.title,
                    contentLength: sectionsToProcess[0]?.content?.length,
                    hasContent: !!sectionsToProcess[0]?.content
                  });
                }

                // 🚨 PROCESS SECTIONS IF FOUND
                if (sectionsToProcess && sectionsToProcess.length > 0) {
                  sectionsReceived = true; // 🚨 FIX: Mark that we received sections
                  sectionsReceivedRef.current = true; // Also set the ref
                  console.log("🚨 NORMAL REGEN: PROCESSING REGENERATED SECTIONS:", sectionsToProcess.length);
                  console.log("🚨 CRITICAL: sectionsReceived flag set to:", sectionsReceived);
                  console.log("🚨 CRITICAL: sectionsReceivedRef.current set to:", sectionsReceivedRef.current);
                  console.log("🚨 NORMAL REGEN: Section titles:", sectionsToProcess.map((s: any) => s.title));

                  // 🚨 FIXED: Deduplicate sections, filter out empty content, and remove dummy images
                  const uniqueSections = new Map();
                  const validSections: any[] = [];

                  sectionsToProcess.forEach((section: any, index: number) => {
                    const sectionKey = section.title?.trim().toLowerCase() || `section-${index + 1}`;
                    const hasContent = section.content && section.content.trim().length > 0;

                    // 🚨 ENHANCED: Extract real images first, then clean content and merge back
                    const existingSection = editableSections.find(s => s.id === section.id);
                    const originalImages = existingSection ? extractImagesFromContent(existingSection.content) : [];

                    let cleanedContent = section.content || '';
                    if (cleanedContent) {
                      // Remove ALL image placeholders (both dummy and real) - we'll add real ones back
                      cleanedContent = cleanedContent
                        .replace(/!\[([^\]]*)\]\([^)]*\)/gi, '') // Remove all markdown images
                        .replace(/<img[^>]*>/gi, '') // Remove all HTML images
                        .replace(/<!-- Preserved Images -->/gi, '') // Remove image section markers
                        .trim();

                      // Add back only real images at the end
                      if (originalImages.length > 0) {
                        cleanedContent += "\n\n<!-- Preserved Images -->\n";
                        originalImages.forEach((image) => {
                          cleanedContent += `\n${image}\n`;
                        });
                        console.log(`🖼️ Preserved ${originalImages.length} real images in section: ${section.title}`);
                      }

                      // Final cleanup: Remove any remaining placeholder text that might have been generated
                      cleanedContent = cleanedContent
                        .replace(/\[Image not available\]/gi, '')
                        .replace(/\[Mission Statement Image\]/gi, '')
                        .replace(/\[Company Logo\]/gi, '')
                        .replace(/\[Team Photo\]/gi, '')
                        .replace(/\[Project Image\]/gi, '')
                        .replace(/\[Sample Image\]/gi, '')
                        .replace(/\[Example Image\]/gi, '')
                        .replace(/\[Placeholder Image\]/gi, '')
                        .replace(/Image:\s*\[.*?\]/gi, '')
                        .trim();
                    }

                    console.log(`🔍 Validating section "${section.title}":`, {
                      hasContent,
                      contentLength: section.content?.length || 0,
                      cleanedContentLength: cleanedContent.length,
                      isDuplicate: uniqueSections.has(sectionKey)
                    });

                    // Only add if not duplicate and has content after cleaning
                    if (!uniqueSections.has(sectionKey) && cleanedContent.length > 0) {
                      uniqueSections.set(sectionKey, true);
                      validSections.push({
                        ...section,
                        content: cleanedContent
                      });
                      console.log(`✅ Added valid section: "${section.title}"`);
                    } else {
                      console.log(`⚠️ Skipped section: "${section.title}" (duplicate: ${uniqueSections.has(sectionKey)}, empty: ${cleanedContent.length === 0})`);
                    }
                  });

                  console.log(`📊 Filtered ${sectionsToProcess.length} → ${validSections.length} valid sections`);

                  // Process valid sections with regenerated flag - ENSURE FLAG IS NOT OVERWRITTEN
                  const regeneratedSections = validSections.map((section: any, index: number) => {
                    // 🚨 CRITICAL: Log what content we're receiving from server
                    console.log(`🔍 SECTION ${index} CONTENT DEBUG:`, {
                      hasContent: !!section.content,
                      contentLength: section.content?.length || 0,
                      contentPreview: section.content?.substring(0, 100) || 'NO CONTENT',
                      title: section.title,
                      allKeys: Object.keys(section)
                    });
                    
                    // Find existing section to preserve structure
                    const existingSection = editableSections.find(s => 
                      s.id === section.id || 
                      s.title === section.title || 
                      s.order === section.order ||
                      s.order === index
                    );
                    
                    const processedSection = {
                      // Start with existing section structure
                      ...(existingSection || {}),
                      // Override with new data from server
                      ...section,
                      // Ensure required fields
                      id: section.id || existingSection?.id || `section-${index + 1}`,
                      title: section.title || existingSection?.title || `Section ${index + 1}`,
                      level: section.level || existingSection?.level || 1,
                      order: section.order !== undefined ? section.order : (existingSection?.order !== undefined ? existingSection.order : index),
                      // 🚨 CRITICAL: Handle content properly
                      content: section.hasOwnProperty('content') ? section.content : (existingSection?.content || ''),
                    fallback: false,
                    regeneratedAt: new Date().toISOString(),
                      regenerated: true, // SET LAST to ensure it's not overwritten
                      edited: true, // 🚨 CRITICAL: Mark as edited so save function recognizes it
                    };
                    
                    console.log(`🚨 FINAL SECTION ${index}:`, {
                      title: processedSection.title,
                      contentLength: processedSection.content?.length || 0,
                      contentChanged: processedSection.content !== existingSection?.content,
                      regenerated: processedSection.regenerated,
                      contentPreview: processedSection.content?.substring(0, 100) || 'NO CONTENT'
                    });
                    
                    return processedSection;
                  });

                  console.log("🚨 NORMAL REGEN: PROCESSED SECTIONS:", regeneratedSections.length);
                  console.log("🚨 NORMAL REGEN: FIRST SECTION CONTENT:", regeneratedSections[0]?.content?.substring(0, 300) + "...");
                  console.log("🚨 NORMAL REGEN: FIRST SECTION REGENERATED FLAG:", regeneratedSections[0]?.regenerated);
                  console.log("🚨 NORMAL REGEN: ALL SECTIONS REGENERATED FLAGS:", regeneratedSections.map(s => ({ title: s.title, regenerated: s.regenerated })));

                  // 🚨 CRITICAL FIX: Save regenerated content to sessionStorage for reload persistence
                  console.log("🚨 NORMAL REGEN: UPDATING STATE WITH REGENERATED SECTIONS...");
                  console.log("🚨 NORMAL REGEN: Current editableSections before update:", editableSections.length);

                  // 🚨 CRITICAL FIX: Aggressive UI update strategy for first regeneration
                  console.log("🚨 NORMAL REGEN: BEFORE STATE UPDATE - editableSections.length:", editableSections.length);

                  // 🚨 IMPROVED: Single, reliable state update with proper progress completion
                  setEditableSections(regeneratedSections);

                  // 🚨 CRITICAL: Also update the ref immediately
                  editableSectionsRef.current = regeneratedSections;
                  console.log("🚨 UPDATED editableSectionsRef with regenerated sections");

                  // Set progress to 100% to indicate completion
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    progress: 100,
                    message: "Regeneration completed successfully!",
                    currentStep: "completed",
                  }));
                  
                  // Mark regeneration as completed
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    isGenerating: false,
                  }));
                  
                  console.log("🚨 REGENERATION COMPLETED SUCCESSFULLY:", {
                    sectionsCount: regeneratedSections.length,
                    firstSectionTitle: regeneratedSections[0]?.title,
                    regenerationCompleted: true,
                    sectionsReceived: true
                  });
                  
                  // 🚨 CRITICAL: Show immediate success feedback
                  toast({
                    title: "Regeneration Complete!",
                    description: `Successfully regenerated ${regeneratedSections.length} sections with new content.`,
                    duration: 3000,
                  });

                  console.log("🚨 NORMAL REGEN: ALL STATE UPDATES INITIATED - UI SHOULD REFRESH NOW");

                  // 🚨 CRITICAL: Save regenerated content to sessionStorage for reload persistence
                  try {
                    const existingSessionData = sessionStorage.getItem("scopingai_proposal_data");
                    const sessionData = existingSessionData ? JSON.parse(existingSessionData) : {};

                    const updatedSessionData = {
                      ...sessionData,
                      // Save regenerated sections for reload persistence
                      regeneratedSections: regeneratedSections,
                      isRegenerated: true,
                      regeneratedAt: new Date().toISOString(),
                      regeneratedTimestamp: Date.now(),
                      // Keep original document info for reference
                      originalDocument: sessionData.originalDocument || regenerationData.existingDocument
                    };

                    sessionStorage.setItem("scopingai_proposal_data", JSON.stringify(updatedSessionData));
                    console.log("🚨 REGENERATED CONTENT SAVED TO SESSION STORAGE FOR RELOAD PERSISTENCE");
                    console.log("🚨 Saved sections count:", regeneratedSections.length);
                  } catch (error) {
                    console.error("❌ Failed to save regenerated content to sessionStorage:", error);
                  }

                  // Force multiple re-renders to ensure UI updates
                  setTimeout(() => {
                    console.log("🚨 FORCE UPDATE 1: Setting sections again");
                    setEditableSections([...regeneratedSections]);
                  }, 100);

                  setTimeout(() => {
                    console.log("🚨 FORCE UPDATE 2: Final state confirmation");
                    setEditableSections(prev => {
                      console.log("🚨 Final state update - Previous length:", prev.length);
                      console.log("🚨 Final state update - New length:", regeneratedSections.length);
                      console.log("🚨 Final state update - Regenerated count:", regeneratedSections.filter(s => s.regenerated).length);
                      return [...regeneratedSections];
                    });
                  }, 200);

                  console.log("🚨 AGGRESSIVE STATE UPDATE INITIATED - UI SHOULD REFRESH");
                }

                if (data.error) {
                  throw new Error(data.error);
                }
              } catch (parseError) {
                console.error("❌ CRITICAL: Failed to parse SSE data:", parseError);
                console.error("❌ Raw line length:", line.length);
                console.error("❌ Raw line (first 500 chars):", line.substring(0, 500));
                console.error("❌ Raw line (last 500 chars):", line.substring(line.length - 500));
                
                // 🚨 CRITICAL: If this is a large payload (likely the sections), try to extract sections manually
                if (line.length > 100000) {
                  console.log("🔧 LARGE PAYLOAD DETECTED - ATTEMPTING MANUAL SECTION EXTRACTION");
                  
                  // Look for sections array pattern in the corrupted JSON
                  const sectionsPattern = /"sections"\s*:\s*\[/;
                  const sectionsMatch = line.match(sectionsPattern);
                  
                  if (sectionsMatch) {
                    console.log("🔧 FOUND SECTIONS PATTERN - ATTEMPTING EXTRACTION");
                    try {
                      // Find the start of the sections array
                      const sectionsStart = line.indexOf('"sections":[') + '"sections":'.length;
                      let arrayDepth = 0;
                      let sectionsEnd = -1;
                      
                      // Find the end of the sections array by counting brackets
                      for (let i = sectionsStart; i < line.length; i++) {
                        if (line[i] === '[') arrayDepth++;
                        if (line[i] === ']') {
                          arrayDepth--;
                          if (arrayDepth === 0) {
                            sectionsEnd = i + 1;
                            break;
                          }
                        }
                      }
                      
                      if (sectionsEnd > sectionsStart) {
                        const sectionsArray = line.substring(sectionsStart, sectionsEnd);
                        console.log("🔧 EXTRACTED SECTIONS ARRAY LENGTH:", sectionsArray.length);
                        
                        const sections = JSON.parse(sectionsArray);
                        console.log("✅ SUCCESSFULLY PARSED SECTIONS FROM CORRUPTED JSON:", sections.length);
                        
                        // Manually process these sections
                        if (sections && Array.isArray(sections) && sections.length > 0) {
                          sectionsReceived = true;
                          sectionsReceivedRef.current = true;
                          
                          const regeneratedSections = sections.map((section: any, index: number) => {
                            // 🚨 CRITICAL: Log manual extraction content
                            console.log(`🔧 MANUAL EXTRACTION SECTION ${index}:`, {
                              hasContent: !!section.content,
                              contentLength: section.content?.length || 0,
                              contentPreview: section.content?.substring(0, 100) || 'NO CONTENT',
                              title: section.title,
                              allKeys: Object.keys(section)
                            });
                            
                            // Find existing section
                            const existingSection = editableSections.find(s => 
                              s.id === section.id || 
                              s.title === section.title || 
                              s.order === section.order ||
                              s.order === index
                            );
                            
                            return {
                              // Start with existing section structure  
                              ...(existingSection || {}),
                              // Override with extracted data
                              ...section,
                              id: section.id || existingSection?.id || `section-${index + 1}`,
                              title: section.title || existingSection?.title || `Section ${index + 1}`,
                              level: section.level || existingSection?.level || 1,
                              order: section.order !== undefined ? section.order : (existingSection?.order !== undefined ? existingSection.order : index),
                              // 🚨 CRITICAL: Preserve extracted content
                              content: section.hasOwnProperty('content') ? section.content : (existingSection?.content || ''),
                              fallback: false,
                              regeneratedAt: new Date().toISOString(),
                              regenerated: true,
                              edited: true, // 🚨 CRITICAL: Mark as edited so save function recognizes it
                            };
                          });
                          
                          console.log("🔧 MANUAL PROCESSING COMPLETE - UPDATING UI:", regeneratedSections.length);
                          setEditableSections(regeneratedSections);
                          editableSectionsRef.current = regeneratedSections;
                          
                          setRegenerationProgress((prev) => ({
                            ...prev,
                            progress: 100,
                            message: "Regeneration completed successfully!",
                            currentStep: "completed",
                          }));
                        }
                      }
                    } catch (extractError) {
                      console.error("❌ Manual extraction failed:", extractError);
                    }
                  }
              }
            }
          }
        }
      }

        // 🚨 CRITICAL: Process any remaining buffered data
        if (incompleteBuffer.trim() && incompleteBuffer.startsWith('data: ')) {
          console.log("🔧 PROCESSING FINAL BUFFERED DATA:", incompleteBuffer.length);
          try {
            let jsonString = incompleteBuffer.slice(6).trim();
            const data = JSON.parse(jsonString);
            console.log("📡 Final buffered SSE data received:", data);
            
            // Apply the same section detection logic to buffered data
            if (data.sections && Array.isArray(data.sections) && data.sections.length > 0) {
              console.log("✅ FOUND SECTIONS IN BUFFERED DATA:", data.sections.length);
              sectionsReceived = true;
              sectionsReceivedRef.current = true;
              
              const regeneratedSections = data.sections.map((section: any, index: number) => {
                // 🚨 CRITICAL: Log buffered content
                console.log(`🔧 BUFFERED SECTION ${index}:`, {
                  hasContent: !!section.content,
                  contentLength: section.content?.length || 0,
                  contentPreview: section.content?.substring(0, 100) || 'NO CONTENT',
                  title: section.title,
                  allKeys: Object.keys(section)
                });
                
                // Find existing section
                const existingSection = editableSections.find(s => 
                  s.id === section.id || 
                  s.title === section.title || 
                  s.order === section.order ||
                  s.order === index
                );
                
                return {
                  // Start with existing section structure
                  ...(existingSection || {}),
                  // Override with buffered data
                  ...section,
                  id: section.id || existingSection?.id || `section-${index + 1}`,
                  title: section.title || existingSection?.title || `Section ${index + 1}`,
                  level: section.level || existingSection?.level || 1,
                  order: section.order !== undefined ? section.order : (existingSection?.order !== undefined ? existingSection.order : index),
                  // 🚨 CRITICAL: Preserve buffered content
                  content: section.hasOwnProperty('content') ? section.content : (existingSection?.content || ''),
                  fallback: false,
                  regeneratedAt: new Date().toISOString(),
                  regenerated: true,
                  edited: true, // 🚨 CRITICAL: Mark as edited so save function recognizes it
                };
              });
              
              setEditableSections(regeneratedSections);
              editableSectionsRef.current = regeneratedSections;
            }
          } catch (bufferError) {
            console.error("❌ Failed to parse buffered data:", bufferError);
          }
        }
      }

      // 🚨 IMPROVED: Better validation and completion detection
      console.log("🔍 CRITICAL FINAL VALIDATION - sectionsReceived:", sectionsReceived);
      console.log("🔍 CRITICAL FINAL VALIDATION - sectionsReceivedRef.current:", sectionsReceivedRef.current);
      console.log("🔍 CRITICAL FINAL VALIDATION - editableSections count:", editableSections.length);
      console.log("🔍 CRITICAL FINAL VALIDATION - editableSections regenerated count:", editableSections.filter(s => s.regenerated).length);
      
      if (!sectionsReceived && !sectionsReceivedRef.current) {
        // Check if we have any new content in editableSections (fallback)
        if (editableSections.length > 0) {
          console.log("✅ FALLBACK: No explicit sections received, but editableSections has content - considering successful");
          sectionsReceived = true;
        } else {
          console.warn("⚠️ No sections received and no content in editableSections, restoring original content");
        setEditableSections([...originalSections]);
        throw new Error("No content received from regeneration API");
        }
      } else {
        console.log("✅ NORMAL REGEN: Sections were received during SSE processing - validation passed");
      }
      
      // Additional safety check - ensure progress is marked as complete
      setRegenerationProgress((prev) => ({
        ...prev,
        progress: 100,
        message: "Regeneration completed successfully!",
        currentStep: "completed",
      }));
      
      setRegenerationProgress((prev) => ({
        ...prev,
        isGenerating: false,
      }));

      // Hide modal and show success
      setShowRegenerationModal(false);

      // ✅ Simple UI refresh after regeneration

      // Clean up session storage after successful regeneration
      console.log("✅ Document regenerated successfully, session data preserved until save");
      console.log("✅ UI should now show regenerated content");

      toast({
        title: "Success!",
        description: "Document regenerated successfully! Check the preview to see new content.",
        duration: 5000,
      });
    } catch (error) {
      console.error("❌ REAL full regeneration failed:", error);

      // Restore original content if regeneration fails
      if (originalSections.length > 0) {
        console.log("🔄 Restoring original content due to regeneration failure");
        setEditableSections([...originalSections]);
      }

      // Hide modal and show error
      setShowRegenerationModal(false);
      toast({
        title: "Regeneration Failed",
        description: `Failed to regenerate document: ${error.message}. Original content has been restored.`,
        variant: "destructive",
      });
    } finally {
      setRegenerationProgress((prev) => ({
        ...prev,
        isGenerating: false,
      }));
    }
  };



  // Handle section regeneration - Show appropriate dialog based on source
  const handleSectionRegeneration = async (
    sectionId: string,
    sectionTitle: string
  ) => {
    console.log("🔄 Section regeneration requested:", { sectionId, sectionTitle });

    // 🚨 VALIDATION: Ensure the section exists in current sections
    const targetSection = editableSections.find(s => s.id === sectionId);
    if (!targetSection) {
      console.error("❌ Section not found in current sections:", sectionId);
      toast({
        title: "Section Not Found",
        description: `The section "${sectionTitle}" could not be found. Please refresh the page and try again.`,
        variant: "destructive",
      });
      return;
    }

    // 🚨 VALIDATION: Ensure regeneration data exists
    if (!regenerationData) {
      console.error("❌ No regeneration data available");
      toast({
        title: "Regeneration Data Missing",
        description: "Required regeneration data is not available. Please go back and start the regeneration process again.",
        variant: "destructive",
      });
      return;
    }

    console.log("✅ Section validation passed, setting selected section");
    setSelectedSectionForRegeneration({ id: sectionId, title: sectionTitle });

    // 🚨 CONDITIONAL: Only show knowledge base popup for "From Existing Proposal" flow
    const isFromExistingProposal = !regenerationData?.fromProposalsPage && !regenerationData?.fromQuickProposal;

    if (isFromExistingProposal) {
      console.log("📋 From Existing Proposal flow - Showing regeneration method popup for section regeneration");
      // Show popup with both options (Knowledge Base & Templates vs AI Prompt) for From Existing Proposal flow
      setPendingRegenerationType('section');
      setPendingSectionId(sectionId);
      setPendingSectionTitle(sectionTitle);
      setShowRegenerationMethodPopup(true);
      console.log('📋 Set pendingRegenerationType to section and showRegenerationMethodPopup to true');
    } else {
      console.log("📋 Quick Proposal or Proposals Page flow - Directly showing AI prompt dialog");
      // For Quick Proposal and Proposals Page flows, directly show AI prompt dialog
      setSelectedSectionForRegeneration({ id: sectionId, title: sectionTitle });
      setShowSectionPromptDialog(true);
    }
  };

  // 🚨 NEW: Handle knowledge base + custom prompt regeneration
  const handleKnowledgeWithPromptRegeneration = async () => {
    if (!knowledgeCustomPrompt.trim() || !regenerationData) return;

    setShowKnowledgePromptDialog(false);

    if (pendingRegenerationType === 'full' || !selectedSectionForRegeneration) {
      // Full document regeneration with knowledge base + custom prompt
      await handleFullRegenerationWithKnowledgePrompt();
    } else {
      // Section regeneration with knowledge base + custom prompt
      await handleSectionRegenerationWithKnowledgePrompt(
        selectedSectionForRegeneration.id,
        selectedSectionForRegeneration.title
      );
    }
  };

  // 🚨 NEW: Handle section regeneration with knowledge base + custom prompt
  const handleSectionRegenerationWithKnowledgePrompt = async (sectionId: string, sectionTitle: string) => {
    if (!regenerationData) {
      console.error("🚨 CRITICAL: No regenerationData available for section regeneration with knowledge base + prompt");
      return;
    }

    console.log("🔄 Starting section regeneration with knowledge base + custom prompt:", {
      sectionId,
      sectionTitle,
      customPrompt: knowledgeCustomPrompt
    });

    // Show progress
    setRegenerationProgress({
      isGenerating: true,
      progress: 0,
      currentStep: "Starting section regeneration with knowledge base + custom prompt...",
      message: `Processing section: ${sectionTitle}`,
      completedSections: [],
    });

    try {
      // Find the section to regenerate
      const sectionToRegenerate = editableSections.find(section => section.id === sectionId);
      if (!sectionToRegenerate) {
        throw new Error(`Section with ID ${sectionId} not found`);
      }

      // 🚨 CRITICAL: Extract images from section content BEFORE sending to AI
      const originalImages = extractImagesFromContent(sectionToRegenerate.content);
      console.log("🖼️ Found", originalImages.length, "images in section:", sectionTitle);

      // Build section regeneration payload with knowledge base + custom prompt (correct structure)
      const sectionRegenerationPayload = {
        existingDocumentId: regenerationData.existingDocument.id,
        sectionId: sectionId,
        sectionTitle: sectionTitle,
        currentContent: sectionToRegenerate.content,
        originalImages: originalImages, // 🚨 CRITICAL: Pass extracted images
        newClientData: regenerationData.newClientData,
        newRequirements: regenerationData.newRequirements,
        selectedKnowledgeDocuments: regenerationData.selectedKnowledgeDocuments || [],
        aiPrompts: {
          customPrompt: {
            content: knowledgeCustomPrompt.trim()
          }
        },
        customPrompt: knowledgeCustomPrompt.trim(), // Add as separate field for backend
        preserveImages: true,
        imageInstructions: "Do not generate any placeholder images, dummy images, or image references like 'Image not available', 'Mission Statement Image', 'Company Logo', etc. Only preserve existing real images.",
      };

      console.log("📋 SECTION REGENERATION WITH KNOWLEDGE + PROMPT PAYLOAD:", JSON.stringify(sectionRegenerationPayload, null, 2));

      // 🚨 DEBUG: Validate payload before sending
      console.log("🔍 PAYLOAD VALIDATION CHECK:");
      console.log("- existingDocumentId:", !!sectionRegenerationPayload.existingDocumentId);
      console.log("- sectionId:", !!sectionRegenerationPayload.sectionId);
      console.log("- newClientData:", !!sectionRegenerationPayload.newClientData, typeof sectionRegenerationPayload.newClientData);
      console.log("- newRequirements:", !!sectionRegenerationPayload.newRequirements, typeof sectionRegenerationPayload.newRequirements);
      console.log("- newClientData keys:", sectionRegenerationPayload.newClientData ? Object.keys(sectionRegenerationPayload.newClientData) : "null");
      console.log("- newRequirements keys:", sectionRegenerationPayload.newRequirements ? Object.keys(sectionRegenerationPayload.newRequirements) : "null");

      // Call section regeneration API endpoint with knowledge base + custom prompt
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tender/regenerate/section/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "scopingai",
            "x-user-id": user?.id || "",
          },
          body: JSON.stringify(sectionRegenerationPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      // Handle Server-Sent Events for real-time progress
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log("📡 Section knowledge + prompt regeneration SSE:", data);

                if (data.progress !== undefined) {
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    progress: data.progress,
                    message: data.message || prev.message,
                    currentStep: data.step || prev.currentStep,
                  }));
                }

                if (data.status === "completed" && data.result) {
                  console.log("✅ Section knowledge + prompt regeneration completed successfully");

                  // 🚨 CRITICAL: Merge images back into regenerated content
                  const regeneratedContentWithImages = mergeImagesIntoContent(
                    data.result.content,
                    originalImages
                  );

                  // Update the specific section with regenerated content + images
                  console.log("🔄 Updating section content in UI:", {
                    sectionId,
                    sectionTitle,
                    oldContentLength: editableSections.find(s => s.id === sectionId)?.content?.length || 0,
                    newContentLength: regeneratedContentWithImages.length,
                    newContentPreview: regeneratedContentWithImages.substring(0, 200) + "..."
                  });

                  setEditableSections(prevSections => {
                    const updatedSections = prevSections.map(section =>
                      section.id === sectionId
                        ? {
                            ...section,
                            content: regeneratedContentWithImages,
                            regenerated: true,
                            regeneratedAt: new Date().toISOString()
                          }
                        : section
                    );

                    console.log("✅ Sections updated, triggering UI refresh");
                    return updatedSections;
                  });

                  // Complete the regeneration
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    isGenerating: false,
                    progress: 100,
                    message: `Section "${sectionTitle}" regenerated successfully with knowledge base and custom instructions!`,
                    currentStep: "completed",
                  }));

                  // 🚨 FORCE UI REFRESH: Force complete UI refresh after content update
                  console.log("🔄 Forcing complete UI refresh after regeneration");

                  // Force re-render by updating a key that will cause components to remount
                  setTimeout(() => {
                    // If the regenerated section is currently selected, refresh the editor
                    if (selectedSectionId === sectionId) {
                      console.log("🔄 Regenerated section is currently selected, forcing editor refresh");
                      setSelectedSectionId(null);
                      setTimeout(() => {
                        setSelectedSectionId(sectionId);
                      }, 50);
                    }

                    // Force a state update to trigger re-render
                    setEditableSections(prev => [...prev]);
                  }, 100);

                  toast({
                    title: "Section Regenerated",
                    description: `"${sectionTitle}" has been regenerated with knowledge base and custom instructions.`,
                  });
                }
              } catch (error) {
                console.error("Error parsing SSE data:", error);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("❌ Section knowledge + prompt regeneration failed:", error);
      setRegenerationProgress((prev) => ({
        ...prev,
        isGenerating: false,
        message: "Section regeneration failed. Please try again.",
      }));

      toast({
        title: "Section Regeneration Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  // 🚨 NEW: Handle full regeneration with knowledge base + custom prompt
  const handleFullRegenerationWithKnowledgePrompt = async () => {
    if (!regenerationData) {
      console.error("🚨 CRITICAL: No regenerationData available for knowledge + prompt regeneration");
      return;
    }

    console.log("🔄 Starting full regeneration with knowledge base + custom prompt:", knowledgeCustomPrompt);

    // Show progress
    setRegenerationProgress({
      isGenerating: true,
      progress: 0,
      currentStep: "Starting knowledge base + custom prompt regeneration...",
      message: "Processing your custom instructions with knowledge base data",
      completedSections: [],
    });

    try {
      // Build the same payload as handleFullRegeneration but add customPrompt
      const regenerationPayload = {
        clientDetails: {
          clientId: regenerationData.newClientData.id,
          name: regenerationData.newClientData.name,
          email: regenerationData.newClientData.email,
          company: regenerationData.newClientData.company,
          industry: regenerationData.newClientData.industry,
          description: regenerationData.newClientData.description
        },
        projectDetails: {
          name: regenerationData.newRequirements.projectTitle || regenerationData.newRequirements.projectName,
          title: regenerationData.newRequirements.projectTitle || regenerationData.newRequirements.projectName,
          description: regenerationData.newRequirements.description || regenerationData.newRequirements.projectDescription,
          objectives: regenerationData.newRequirements.objectives,
          timeline: regenerationData.newRequirements.timeline,
          budget: regenerationData.newRequirements.budget,
          scope: regenerationData.newRequirements.scope
        },
        requirements: regenerationData.selectedKnowledgeDocuments && regenerationData.selectedKnowledgeDocuments.length > 0
          ? {
              type: "knowledge_base",
              documents: regenerationData.selectedKnowledgeDocuments.map(docId => ({
                documentId: docId,
                extractPrompt: regenerationData.documentRequirements?.[docId] || "Get Key information from this document based on the selected template sections."
              }))
            }
          : {
              type: "manual",
              text: regenerationData.newRequirements.description || regenerationData.newRequirements.projectDescription || ""
            },
        templateId: regenerationData.existingDocument.id,
        aiPrompt: knowledgeCustomPrompt.trim(), // Use knowledge custom prompt
        customPrompt: knowledgeCustomPrompt.trim(), // Add as separate field for backend
      };

      console.log("📋 KNOWLEDGE + PROMPT REGENERATION PAYLOAD:", JSON.stringify(regenerationPayload, null, 2));

      // Call regeneration API with knowledge base + custom prompt
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tender/regenerate/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "scopingai",
            "x-user-id": user?.id || "",
          },
          body: JSON.stringify(regenerationPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      // Handle Server-Sent Events for real-time progress (same as existing)
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log("📡 Knowledge + Prompt regeneration SSE:", data);

                if (data.progress !== undefined) {
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    progress: data.progress,
                    message: data.message || prev.message,
                    currentStep: data.step || prev.currentStep,
                    completedSections: data.completedSections || prev.completedSections,
                  }));
                }

                if (data.status === "completed" && data.result) {
                  console.log("✅ Knowledge + Prompt regeneration completed successfully");

                  // Update sections with regenerated content
                  if (data.result.sections) {
                    setEditableSections(data.result.sections);
                    console.log(`🔄 Updated ${data.result.sections.length} sections with regenerated content`);
                  }

                  // Complete the regeneration
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    isGenerating: false,
                    progress: 100,
                    message: "Knowledge base + custom prompt regeneration completed successfully!",
                    currentStep: "completed",
                  }));

                  toast({
                    title: "Regeneration Complete",
                    description: "Document regenerated successfully with knowledge base and custom instructions.",
                  });
                }
              } catch (error) {
                console.error("Error parsing SSE data:", error);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("❌ Knowledge + Prompt regeneration failed:", error);
      setRegenerationProgress((prev) => ({
        ...prev,
        isGenerating: false,
        message: "Regeneration failed. Please try again.",
      }));

      toast({
        title: "Regeneration Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  // 🚨 NEW: Handle regenerate all sections with custom prompt
  const handleRegenerateWithPrompt = async () => {
    if (!customPrompt.trim() || !regenerationData) return;

    setIsRegeneratingWithPrompt(true);
    setShowPromptDialog(false);

    try {
      console.log("🔄 Starting regeneration with custom prompt:", customPrompt);

      // Show progress
      setRegenerationProgress({
        isGenerating: true,
        progress: 0,
        currentStep: "Starting regeneration with custom prompt...",
        message: "Processing your custom prompt for all sections",
        completedSections: [],
      });

      // Call new API endpoint for prompt-based regeneration
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tender/regenerate-with-prompt/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "scopingai",
            "x-user-id": user?.id || "",
          },
          body: JSON.stringify({
            documentId: regenerationData.existingDocument.id,
            sections: editableSections,
            customPrompt: customPrompt.trim(),
            preserveImages: true, // 🚨 CRITICAL: Preserve images
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      // Handle Server-Sent Events for real-time progress
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log("📡 Prompt regeneration SSE:", data);

                if (data.progress !== undefined) {
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    progress: data.progress,
                    message: data.message || prev.message,
                    currentStep: data.step || prev.currentStep,
                    completedSections: data.completedSections || prev.completedSections,
                  }));
                }

                // 🚨 FIX: Process regenerated sections with better detection
                let sectionsToProcess = null;

                // Check multiple possible locations for sections data
                if (data.sections && Array.isArray(data.sections) && data.sections.length > 0) {
                  sectionsToProcess = data.sections;
                  console.log("🚨 Found sections in data.sections:", sectionsToProcess.length);
                } else if (data.status === "completed" && data.sections) {
                  sectionsToProcess = data.sections;
                  console.log("🚨 Found sections in completed event:", sectionsToProcess.length);
                }

                if (sectionsToProcess && sectionsToProcess.length > 0) {
                  console.log("🚨 PROCESSING REGENERATED SECTIONS WITH PROMPT:", sectionsToProcess.length);

                  // 🚨 FIXED: Deduplicate sections, filter out empty content, and remove dummy images for prompt-based regeneration
                  const uniqueSections = new Map();
                  const validSections: any[] = [];

                  sectionsToProcess.forEach((section: any, index: number) => {
                    const sectionKey = section.title?.trim().toLowerCase() || `section-${index + 1}`;
                    const hasContent = section.content && section.content.trim().length > 0;

                    // 🚨 ENHANCED: Extract real images first, then clean content and merge back
                    const existingSection = editableSections.find(s => s.id === section.id);
                    const originalImages = existingSection ? extractImagesFromContent(existingSection.content) : [];

                    let cleanedContent = section.content || '';
                    if (cleanedContent) {
                      // Remove ALL image placeholders (both dummy and real) - we'll add real ones back
                      cleanedContent = cleanedContent
                        .replace(/!\[([^\]]*)\]\([^)]*\)/gi, '') // Remove all markdown images
                        .replace(/<img[^>]*>/gi, '') // Remove all HTML images
                        .replace(/<!-- Preserved Images -->/gi, '') // Remove image section markers
                        .trim();

                      // Add back only real images at the end
                      if (originalImages.length > 0) {
                        cleanedContent += "\n\n<!-- Preserved Images -->\n";
                        originalImages.forEach((image) => {
                          cleanedContent += `\n${image}\n`;
                        });
                        console.log(`🖼️ Preserved ${originalImages.length} real images in section: ${section.title}`);
                      }

                      // Final cleanup: Remove any remaining placeholder text that might have been generated
                      cleanedContent = cleanedContent
                        .replace(/\[Image not available\]/gi, '')
                        .replace(/\[Mission Statement Image\]/gi, '')
                        .replace(/\[Company Logo\]/gi, '')
                        .replace(/\[Team Photo\]/gi, '')
                        .replace(/\[Project Image\]/gi, '')
                        .replace(/\[Sample Image\]/gi, '')
                        .replace(/\[Example Image\]/gi, '')
                        .replace(/\[Placeholder Image\]/gi, '')
                        .replace(/Image:\s*\[.*?\]/gi, '')
                        .trim();
                    }

                    console.log(`🔍 Validating prompt section "${section.title}":`, {
                      hasContent,
                      contentLength: section.content?.length || 0,
                      cleanedContentLength: cleanedContent.length,
                      isDuplicate: uniqueSections.has(sectionKey)
                    });

                    // Only add if not duplicate and has content after cleaning
                    if (!uniqueSections.has(sectionKey) && cleanedContent.length > 0) {
                      uniqueSections.set(sectionKey, true);
                      validSections.push({
                        ...section,
                        content: cleanedContent
                      });
                      console.log(`✅ Added valid prompt section: "${section.title}"`);
                    } else {
                      console.log(`⚠️ Skipped prompt section: "${section.title}" (duplicate: ${uniqueSections.has(sectionKey)}, empty: ${cleanedContent.length === 0})`);
                    }
                  });

                  console.log(`📊 Filtered prompt sections ${sectionsToProcess.length} → ${validSections.length} valid sections`);

                  const regeneratedSections = validSections.map((section: any, index: number) => ({
                    id: section.id || `section-${index + 1}`,
                    title: section.title || `Section ${index + 1}`,
                    content: section.content.trim(),
                    level: section.level || 1,
                    order: index,
                    regenerated: true,
                    regeneratedWithPrompt: true,
                    customPrompt: customPrompt,
                    regeneratedAt: new Date().toISOString(),
                  }));

                  console.log("🚨 UPDATING UI WITH REGENERATED SECTIONS:", regeneratedSections.length);
                  console.log("🚨 First regenerated section:", regeneratedSections[0]?.title, regeneratedSections[0]?.content?.substring(0, 100) + "...");

                  // 🚨 AGGRESSIVE UI UPDATE STRATEGY
                  setEditableSections([...regeneratedSections]); // Force new array reference

                  // Force additional update after short delay
                  setTimeout(() => {
                    setEditableSections([...regeneratedSections]);
                    
                    console.log("🚨 FORCED ADDITIONAL UI UPDATE");
                  }, 100);

                  // Save to session storage for persistence
                  try {
                    const existingSessionData = sessionStorage.getItem("scopingai_proposal_data");
                    const sessionData = existingSessionData ? JSON.parse(existingSessionData) : {};

                    const updatedSessionData = {
                      ...sessionData,
                      regeneratedSections: regeneratedSections,
                      isRegenerated: true,
                      regeneratedWithPrompt: true,
                      customPrompt: customPrompt,
                      regeneratedAt: new Date().toISOString(),
                    };

                    sessionStorage.setItem("scopingai_proposal_data", JSON.stringify(updatedSessionData));
                    console.log("🚨 PROMPT REGENERATED CONTENT SAVED TO SESSION STORAGE");
                  } catch (error) {
                    console.error("❌ Failed to save prompt regenerated content:", error);
                  }
                }

                if (data.error) {
                  throw new Error(data.error);
                }
              } catch (parseError) {
                console.warn("Failed to parse prompt regeneration SSE data:", parseError);
              }
            }
          }
        }
      }

      toast({
        title: "Success!",
        description: "All sections regenerated successfully with your custom prompt!",
        duration: 5000,
      });

    } catch (error) {
      console.error("❌ Prompt regeneration failed:", error);
      toast({
        title: "Regeneration Failed",
        description: `Failed to regenerate with prompt: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsRegeneratingWithPrompt(false);
      setCustomPrompt("");
      setRegenerationProgress((prev) => ({
        ...prev,
        isGenerating: false,
      }));
    }
  };

  // 🚨 NEW: Handle regenerate single section with custom prompt
  const handleRegenerateSectionWithPrompt = async () => {
    if (!customPrompt.trim() || !selectedSectionForRegeneration || !regenerationData) return;

    setIsRegeneratingWithPrompt(true);
    setShowSectionPromptDialog(false);

    try {
      console.log("🔄 Starting section regeneration with custom prompt:", {
        section: selectedSectionForRegeneration.title,
        prompt: customPrompt
      });

      // Find the section to regenerate
      const sectionToRegenerate = editableSections.find(s => s.id === selectedSectionForRegeneration.id);
      if (!sectionToRegenerate) {
        throw new Error("Section not found");
      }

      // 🚨 NEW: Extract images from current section content
      const originalImages = extractImagesFromContent(sectionToRegenerate.content);
      console.log("🖼️ Found", originalImages.length, "images in section for prompt regeneration");

      // Show progress
      setRegenerationProgress({
        isGenerating: true,
        progress: 0,
        currentStep: `Regenerating section: ${selectedSectionForRegeneration.title}`,
        message: "Processing your custom prompt and preserving images",
        completedSections: [],
      });

      // 🚨 CRITICAL FIX: Enhanced API call with better error handling and validation
      console.log("🔍 Section regeneration API call details:", {
        endpoint: `${import.meta.env.VITE_API_URL}/api/tender/regenerate-section-with-prompt/stream`,
        userId: user?.id,
        documentId: regenerationData.existingDocument.id,
        sectionId: selectedSectionForRegeneration.id,
        sectionTitle: selectedSectionForRegeneration.title,
        hasContent: !!sectionToRegenerate.content,
        contentLength: sectionToRegenerate.content?.length || 0,
        customPromptLength: customPrompt.trim().length,
        originalImagesCount: originalImages.length
      });

      // Call new API endpoint for section prompt-based regeneration
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tender/regenerate-section-with-prompt/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "scopingai",
            "x-user-id": user?.id || "",
          },
          body: JSON.stringify({
            documentId: regenerationData.existingDocument.id,
            sectionId: selectedSectionForRegeneration.id,
            sectionTitle: selectedSectionForRegeneration.title,
            currentContent: sectionToRegenerate.content || "",
            originalImages: originalImages, // 🚨 NEW: Include extracted images
            customPrompt: customPrompt.trim(),
            preserveImages: true, // 🚨 CRITICAL: Preserve images
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      // Handle Server-Sent Events for real-time progress
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log("📡 Section prompt regeneration SSE:", data);

                if (data.progress !== undefined) {
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    progress: data.progress,
                    message: data.message || prev.message,
                    currentStep: data.step || prev.currentStep,
                  }));
                }

                // 🚨 FIX: Process regenerated section only when completed - use consistent format
                if (data.status === 'completed' && data.result && data.result.content) {
                  console.log("🚨 RECEIVED COMPLETED REGENERATED SECTION WITH PROMPT:", data.result.section_title);

                  // 🚨 NEW: Merge images back into regenerated content
                  const regeneratedContentWithImages = mergeImagesIntoContent(
                    data.result.content,
                    originalImages
                  );

                  // 🚨 ROBUST UPDATE: Update the specific section in the array
                  console.log("🔍 Updating section with prompt regeneration:", selectedSectionForRegeneration.id);
                  console.log("📄 New content length:", regeneratedContentWithImages?.length || 0);

                  const updatedSections = editableSections.map(section => {
                    if (section.id === selectedSectionForRegeneration.id) {
                      console.log("🎯 Found matching section for prompt update:", section.title);
                      return {
                        ...section,
                        content: regeneratedContentWithImages,
                        regenerated: true,
                        regeneratedWithPrompt: true,
                        customPrompt: customPrompt,
                        regeneratedAt: new Date().toISOString(),
                        lastModified: new Date().toISOString(),
                        edited: true, // Mark as edited to ensure UI updates
                      };
                    }
                    return section;
                  });

                  console.log("✅ Sections updated with prompt regeneration");
                  // Update UI with regenerated section
                  setEditableSections(updatedSections);

                  // Also update the ref
                  editableSectionsRef.current = updatedSections;

                  // 🚨 CRITICAL FIX: Force editor re-render by updating preview trigger and editor key
                  setPreviewUpdateTrigger(prev => prev + 1);

                  // Force editor re-initialization with a slight delay to ensure state is updated
                  setTimeout(() => {
                    setEditorKey(prev => prev + 1);
                    console.log("🔄 FORCED EDITOR KEY UPDATE after regeneration");
                  }, 100);

                  // 🚨 CRITICAL FIX: Force editor re-render by updating preview trigger
                  setPreviewUpdateTrigger(prev => prev + 1);

                  // 🚨 CRITICAL FIX: Ensure initialization state allows saving after regeneration
                  setInitializationState(prev => ({
                    ...prev,
                    canSave: true,
                    isInitializing: false,
                    hasError: false,
                    errorMessage: ""
                  }));

                  // Save to session storage for persistence
                  try {
                    const existingSessionData = sessionStorage.getItem("scopingai_proposal_data");
                    const sessionData = existingSessionData ? JSON.parse(existingSessionData) : {};

                    const updatedSessionData = {
                      ...sessionData,
                      regeneratedSections: updatedSections,
                      isRegenerated: true,
                      regeneratedAt: new Date().toISOString(),
                    };

                    sessionStorage.setItem("scopingai_proposal_data", JSON.stringify(updatedSessionData));
                    console.log("🚨 SECTION PROMPT REGENERATED CONTENT SAVED TO SESSION STORAGE");
                  } catch (error) {
                    console.error("❌ Failed to save section regenerated content:", error);
                  }
                }

                if (data.error) {
                  throw new Error(data.error);
                }
              } catch (parseError) {
                console.warn("Failed to parse section regeneration SSE data:", parseError);
              }
            }
          }
        }
      }

      // 🚨 ROBUST FOCUS FIX: Keep focus on the regenerated section
      console.log("🎯 Setting focus to regenerated section:", selectedSectionForRegeneration.id);
      setSelectedSectionId(selectedSectionForRegeneration.id);

      // Force focus with a slight delay to ensure state has updated
      setTimeout(() => {
        console.log("🎯 Double-checking focus on section:", selectedSectionForRegeneration.id);
        setSelectedSectionId(selectedSectionForRegeneration.id);
      }, 100);

      toast({
        title: "Success!",
        description: `Section "${selectedSectionForRegeneration.title}" regenerated successfully with your custom prompt!`,
        duration: 5000,
      });

    } catch (error) {
      console.error("❌ Section prompt regeneration failed:", error);
      toast({
        title: "Section Regeneration Failed",
        description: `Failed to regenerate section with prompt: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsRegeneratingWithPrompt(false);
      setCustomPrompt("");
      setSelectedSectionForRegeneration(null);
      setRegenerationProgress((prev) => ({
        ...prev,
        isGenerating: false,
      }));
    }
  };

  // Handle section title editing
  const toggleTitleEditing = (sectionId: string) => {
    const newEditing = new Set(editingTitles);
    if (newEditing.has(sectionId)) {
      newEditing.delete(sectionId);
      // Save the title when exiting edit mode
      const newTitle = titleInputs[sectionId];
      if (newTitle && newTitle.trim()) {
        setEditableSections(prev => 
          prev.map(section => 
            section.id === sectionId ? { ...section, title: newTitle.trim() } : section
          )
        );
      }
    } else {
      newEditing.add(sectionId);
      // Initialize the input with current title
      const section = editableSections.find(s => s.id === sectionId);
      if (section) {
        setTitleInputs(prev => ({
          ...prev,
          [sectionId]: section.title
        }));
      }
    }
    setEditingTitles(newEditing);
  };

  const handleTitleChange = (sectionId: string, newTitle: string) => {
    setTitleInputs(prev => ({
      ...prev,
      [sectionId]: newTitle
    }));
  };

  const handleTitleSave = (sectionId: string) => {
    const newTitle = titleInputs[sectionId];
    if (newTitle && newTitle.trim()) {
      // Trim and limit title length to prevent menu breaking
      const trimmedTitle = newTitle.trim().substring(0, 100); // Max 100 characters
      setEditableSections(prev =>
        prev.map(section =>
          section.id === sectionId ? { ...section, title: trimmedTitle } : section
        )
      );
    }
    setEditingTitles(prev => {
      const newEditing = new Set(prev);
      newEditing.delete(sectionId);
      return newEditing;
    });
  };

  const handleTitleCancel = (sectionId: string) => {
    setEditingTitles(prev => {
      const newEditing = new Set(prev);
      newEditing.delete(sectionId);
      return newEditing;
    });
    // Reset the input to original title
    const section = editableSections.find(s => s.id === sectionId);
    if (section) {
      setTitleInputs(prev => ({
        ...prev,
        [sectionId]: section.title
      }));
    }
  };

  const handleDeleteSection = (sectionId: string) => {
    setEditableSections(prev => {
      const updatedSections = prev.filter(section => section.id !== sectionId);
      
      // If this was the selected section, clear the selection
      if (selectedSectionId === sectionId) {
        setSelectedSectionId(null);
      }
      
      return updatedSections;
    });
  };

  // Add new section at the end
  const handleAddSection = () => {
    const newSectionId = `section-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newSection = {
      id: newSectionId,
      title: "New Section".trim(),
      content: "",
      level: 1,
      order: editableSections.length,
    };

    setEditableSections(prev => [...prev, newSection]);
    setSelectedSectionId(newSectionId);
    
    toast({
      title: "Section Added",
      description: "New section added successfully",
      duration: 2000,
    });
  };

  // Add new section after a specific section
  const handleAddSectionAfter = (afterSectionId: string) => {
    const afterSectionIndex = editableSections.findIndex(s => s.id === afterSectionId);
    if (afterSectionIndex === -1) return;

    const newSectionId = `section-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newSection = {
      id: newSectionId,
      title: "New Section".trim(),
      content: "",
      level: 1,
      order: afterSectionIndex + 1,
    };

    setEditableSections(prev => {
      const updatedSections = [...prev];
      updatedSections.splice(afterSectionIndex + 1, 0, newSection);
      
      // Update order for sections after the insertion point
      updatedSections.forEach((section, index) => {
        if (index > afterSectionIndex) {
          section.order = index;
        }
      });
      
      return updatedSections;
    });

    setSelectedSectionId(newSectionId);
    
    toast({
      title: "Section Added",
      description: "New section added after the selected section",
      duration: 2000,
    });
  };

  // 🚨 NEW: Project title editing functions
  const toggleProjectTitleEditing = () => {
    if (isEditingProjectTitle) {
      // Cancel editing
      setIsEditingProjectTitle(false);
      setProjectTitleInput("");
    } else {
      // Start editing
      setIsEditingProjectTitle(true);
      setProjectTitleInput(regenerationData?.existingDocument?.title || "");
    }
  };

  const handleProjectTitleChange = (newTitle: string) => {
    setProjectTitleInput(newTitle);
  };

  const handleProjectTitleSave = () => {
    if (regenerationData && projectTitleInput.trim()) {
      setRegenerationData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          existingDocument: {
            ...prev.existingDocument,
            title: projectTitleInput.trim()
          }
        };
      });
      setIsEditingProjectTitle(false);
      setProjectTitleInput("");
      
      toast({
        title: "Project Title Updated",
        description: "Project title has been updated successfully",
        duration: 2000,
      });
    }
  };

  const handleProjectTitleCancel = () => {
    setIsEditingProjectTitle(false);
    setProjectTitleInput("");
  };

  // 🚨 NEW: Helper function to determine if save should be disabled
  const isSaveDisabled = () => {
    return (
      initializationState.isInitializing ||
      initializationState.hasError ||
      !initializationState.canSave ||
      !regenerationData ||
      editableSections.length === 0 ||
      isSaving
    );
  };

  // 🚨 NEW: Helper function to get save button tooltip
  const getSaveButtonTooltip = () => {
    if (initializationState.isInitializing) return "Loading document data...";
    if (initializationState.hasError) return `Error: ${initializationState.errorMessage}`;
    if (!regenerationData) return "Document data not available";
    if (editableSections.length === 0) return "No sections to save";
    if (isSaving) return "Saving in progress...";
    return "";
  };

  // Handle section content editing with Yoopta Editor
  const handleSectionContentChange = useCallback((sectionId: string, newMarkdownContent: string) => {
    console.log('🔄 STEP 1 - handleSectionContentChange called:', {
      sectionId,
      contentLength: newMarkdownContent?.length || 0,
      hasContent: !!(newMarkdownContent && newMarkdownContent.trim()),
      timestamp: new Date().toISOString()
    });

    try {
      // Update editableSections with new markdown content and regenerate yooptaValue
      setEditableSections(prev => {


        const updatedSections = prev.map(section => {
          if (section.id === sectionId) {
            // Prevent accidental empty content from corrupting data
            if (!newMarkdownContent || newMarkdownContent.trim() === '') {
              // Only prevent update if we have existing content AND user is not actively editing
              if (section.content && section.content.trim() && !isEditing) {
                return section; // Preserve existing content
              }
            }

            // Convert the new markdown to Yoopta format for future edits
            let newYooptaValue;
            try {
              newYooptaValue = convertMarkdownToYoopta(newMarkdownContent);
            } catch (conversionError) {
              console.error('Failed to convert markdown to Yoopta:', conversionError);
              newYooptaValue = section.yooptaValue; // Fallback
            }

          const updatedSection = {
            ...section,
            content: newMarkdownContent, // Update markdown content
            yooptaValue: newYooptaValue, // Update Yoopta value
            originalContent: section.originalContent || section.content, // Preserve original
            edited: true,
            lastModified: Date.now() // Add timestamp for change tracking
          };



          return updatedSection;
        }
        return section;
      });

      // Update ref immediately with the new content
      editableSectionsRef.current = updatedSections;
      
      console.log('🔄 STEP 2 - Updated editableSectionsRef:', {
        sectionId,
        refSectionsCount: editableSectionsRef.current.length,
        updatedSectionContent: editableSectionsRef.current.find(s => s.id === sectionId)?.content?.length || 0,
        allEditedSections: editableSectionsRef.current.filter(s => s.edited).length
      });



      return updatedSections;
    });

    // Force preview update
    setTimeout(() => {
      setPreviewUpdateTrigger(prev => prev + 1);
    }, 100);
    } catch (error) {
      console.error('Error updating section content:', error);
      toast({
        title: "Error",
        description: "Failed to update section content. Please try again.",
        variant: "destructive",
      });
    }
  }, []);

  // Force preview update when selected section changes
  useEffect(() => {
    if (selectedSection) {
      setPreviewUpdateTrigger(prev => prev + 1);
    }
  }, [selectedSection?.id]);

  // Force preview update when any section content changes
  useEffect(() => {
    if (selectedSection && !isEditing) {
      setPreviewUpdateTrigger(prev => prev + 1);
    }
  }, [selectedSection?.id, isEditing]);

  // Toggle edit mode for a section
  const toggleEditMode = useCallback((sectionId: string) => {
    if (isEditing && selectedSectionId !== sectionId) {
      // Save current editing content if switching sections
      const currentSection = editableSections.find(s => s.id === selectedSectionId);
      if (currentSection && editingContent[selectedSectionId]) {
        setEditableSections(prev => 
          prev.map(section => 
            section.id === selectedSectionId 
              ? { ...section, content: editingContent[selectedSectionId], edited: true }
              : section
          )
        );
      }
    }

    // Find the target section first
    const targetSection = editableSections.find(s => s.id === sectionId);
    
    if (!targetSection) {
      console.error('No section found with ID:', sectionId);
      return;
    }

    // Force editor re-initialization when switching to edit mode
    if (!isEditing) {
      setEditorKey(prev => prev + 1);
    }

    if (!isEditing) {
      // Starting to edit
      const currentContent = editingContent[sectionId] || targetSection.content || '';
      
      setSelectedSectionId(sectionId);
      setEditingContent(prev => ({
        ...prev,
        [sectionId]: currentContent
      }));
      setIsEditing(true);
    } else {
      // Stopping edit mode
      setIsEditing(false);
    }
  }, [isEditing, selectedSectionId, editableSections, editingContent]);

  // 🚨 FIXED: Extract only real images from content (no placeholders)
  const extractImagesFromContent = (content: string) => {
    const images = [];

    // Extract base64 images (both markdown and HTML format)
    const base64ImageRegex = /!\[([^\]]*)\]\(data:image\/[^;]+;base64,[^)]+\)|<img[^>]+src=["']data:image\/[^;]+;base64,[^"']+["'][^>]*>/g;
    let match: RegExpExecArray | null;

    while ((match = base64ImageRegex.exec(content)) !== null) {
      const imageMatch = match[0];

      // 🚨 ENHANCED: Skip dummy/placeholder images with comprehensive filtering
      const altText = match[1] || '';
      const isDummyImage = altText.toLowerCase().includes('not available') ||
                          altText.toLowerCase().includes('placeholder') ||
                          altText.toLowerCase().includes('image not') ||
                          altText.toLowerCase().includes('mission statement image') ||
                          altText.toLowerCase().includes('company logo') ||
                          altText.toLowerCase().includes('team photo') ||
                          altText.toLowerCase().includes('project image') ||
                          altText.toLowerCase().includes('sample image') ||
                          altText.toLowerCase().includes('example image') ||
                          altText.toLowerCase().includes('dummy') ||
                          altText.toLowerCase().includes('temp') ||
                          imageMatch.includes('placeholder') ||
                          imageMatch.includes('data:image/svg') || // Skip SVG placeholders
                          imageMatch.includes('1x1') || // Skip tracking pixels
                          imageMatch.includes('pixel.gif'); // Skip tracking pixels

      if (!isDummyImage) {
        images.push(imageMatch);
        console.log("✅ Preserved real image:", altText);
      } else {
        console.log("⚠️ Skipped dummy image:", altText);
      }
    }

    // Also extract HTTP/HTTPS images (but skip placeholders)
    const urlImageRegex = /!\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g;
    while ((match = urlImageRegex.exec(content)) !== null) {
      const imageMatch = match[0];
      const altText = match[1] || '';
      const imageUrl = match[2] || '';

      // 🚨 ENHANCED: Skip dummy/placeholder URLs with comprehensive filtering
      const isDummyImage = altText.toLowerCase().includes('not available') ||
                          altText.toLowerCase().includes('placeholder') ||
                          altText.toLowerCase().includes('image not') ||
                          altText.toLowerCase().includes('mission statement image') ||
                          altText.toLowerCase().includes('company logo') ||
                          altText.toLowerCase().includes('team photo') ||
                          altText.toLowerCase().includes('project image') ||
                          altText.toLowerCase().includes('sample image') ||
                          altText.toLowerCase().includes('example image') ||
                          altText.toLowerCase().includes('dummy') ||
                          altText.toLowerCase().includes('temp') ||
                          imageUrl.includes('placeholder') ||
                          imageUrl.includes('example.com') ||
                          imageUrl.includes('dummy') ||
                          imageUrl.includes('temp') ||
                          imageUrl.includes('1x1') ||
                          imageUrl.includes('pixel.gif');

      if (!isDummyImage) {
        images.push(imageMatch);
        console.log("✅ Preserved real URL image:", altText);
      } else {
        console.log("⚠️ Skipped dummy URL image:", altText);
      }
    }

    console.log("🖼️ Extracted real images from content:", images.length);
    return images;
  };

  // 🚨 FIXED: Merge preserved images back into regenerated content (No duplicates, no comments)
  const mergeImagesIntoContent = (regeneratedContent: string, originalImages: string[]) => {
    if (originalImages.length === 0) return regeneratedContent;

    let contentWithImages = regeneratedContent;

    // Only add images that are not already present in the regenerated content
    const imagesToAdd = originalImages.filter(image => {
      // Check if this exact image is already in the content
      return !contentWithImages.includes(image);
    });

    if (imagesToAdd.length > 0) {
      // Add images without comment text, just append them cleanly
      contentWithImages += "\n\n";
      imagesToAdd.forEach((image) => {
        contentWithImages += `${image}\n\n`;
      });
    }

    console.log("🖼️ Merged", imagesToAdd.length, "new images back into regenerated content (", originalImages.length, "total images found)");
    return contentWithImages.trim();
  };

  // 🚨 NEW: Handle section regeneration with knowledge base (like Regenerate All but for single section)
  const handleSectionRegenerationWithKnowledge = async (sectionId: string, sectionTitle: string) => {
    if (!regenerationData) {
      console.error("🚨 CRITICAL: No regenerationData available for section regeneration with knowledge base");
      return;
    }

    console.log("🚨 STARTING handleSectionRegenerationWithKnowledge:", { sectionId, sectionTitle });

    // Find the target section
    const targetSection = editableSections.find(s => s.id === sectionId);
    if (!targetSection) {
      console.error("❌ Section not found in current sections:", sectionId);
      toast({
        title: "Section Not Found",
        description: `The section "${sectionTitle}" could not be found. Please refresh the page and try again.`,
        variant: "destructive",
      });
      return;
    }

    // Extract images from section content
    const originalImages = extractImagesFromContent(targetSection.content);
    console.log("🖼️ Found", originalImages.length, "images in section:", sectionTitle);

    // Show progress
    setRegenerationProgress({
      isGenerating: true,
      progress: 0,
      currentStep: `Regenerating section: ${sectionTitle}`,
      message: "Processing section with knowledge base and template data",
      completedSections: [],
    });

    try {
      // Prepare payload for section regeneration (same structure as full regeneration)
      const sectionRegenerationPayload = {
        existingDocumentId: regenerationData.existingDocument.id,
        sectionId: sectionId,
        sectionTitle: sectionTitle,
        currentContent: targetSection.content,
        originalImages: originalImages,
        newClientData: regenerationData.newClientData,
        newRequirements: regenerationData.newRequirements,
        selectedKnowledgeDocuments: regenerationData.selectedKnowledgeDocuments || [],
        aiPrompts: regenerationData.aiPrompts || {},
        preserveImages: true,
        imageInstructions: "Do not generate any placeholder images, dummy images, or image references like 'Image not available', 'Mission Statement Image', 'Company Logo', etc. Only preserve existing real images.",
      };

      console.log("📋 SECTION REGENERATION WITH KNOWLEDGE PAYLOAD:", JSON.stringify(sectionRegenerationPayload, null, 2));

      // Call section regeneration API endpoint with knowledge base
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tender/regenerate/section/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "scopingai",
            "x-user-id": user?.id || "",
          },
          body: JSON.stringify(sectionRegenerationPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      // Handle Server-Sent Events for real-time progress
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader!.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log("📡 Section regeneration SSE:", data);

              // Handle progress updates
              if (data.progress !== undefined) {
                setRegenerationProgress(prev => ({
                  ...prev,
                  progress: data.progress,
                  message: data.message || prev.message,
                  currentStep: data.step || prev.currentStep,
                }));
              }

              // Handle completion - the backend sends 'completed' event with result
              if (data.status === 'completed' && data.result) {
                console.log("✅ Section regeneration completed:", data.result.section_title);
                console.log("🔍 Updating section with ID:", sectionId);
                console.log("📄 New content length:", data.result.content?.length || 0);

                // 🚨 ROBUST UPDATE: Update the specific section with the regenerated content
                setEditableSections(prev => {
                  console.log("🔄 Current sections before update:", prev.map(s => ({ id: s.id, title: s.title, contentLength: s.content?.length || 0 })));

                  const updatedSections = prev.map(section => {
                    if (section.id === sectionId) {
                      console.log("🎯 Found matching section to update:", section.title);
                      return {
                        ...section,
                        content: data.result.content,
                        regenerated: true,
                        regeneratedAt: new Date().toISOString(),
                        lastModified: new Date().toISOString(),
                        edited: true, // Mark as edited to ensure UI updates
                      };
                    }
                    return section;
                  });

                  console.log("✅ Sections after update:", updatedSections.map(s => ({ id: s.id, title: s.title, contentLength: s.content?.length || 0, regenerated: s.regenerated })));
                  return updatedSections;
                });

                // Also update the ref for saving
                editableSectionsRef.current = editableSectionsRef.current.map(section =>
                  section.id === sectionId
                    ? {
                        ...section,
                        content: data.result.content,
                        regenerated: true,
                        regeneratedAt: new Date().toISOString(),
                        lastModified: new Date().toISOString(),
                        edited: true,
                      }
                    : section
                );

                // Update progress
                setRegenerationProgress(prev => ({
                  ...prev,
                  progress: 100,
                  currentStep: "Section regeneration completed",
                  message: `Successfully regenerated "${sectionTitle}" with knowledge base data`,
                }));

                // 🚨 CRITICAL FIX: Ensure initialization state allows saving after regeneration
                setInitializationState(prev => ({
                  ...prev,
                  canSave: true,
                  isInitializing: false,
                  hasError: false,
                  errorMessage: ""
                }));

                // 🚨 CRITICAL FIX: Force editor re-render by updating preview trigger and editor key
                setPreviewUpdateTrigger(prev => prev + 1);

                // Force editor re-initialization with a slight delay to ensure state is updated
                setTimeout(() => {
                  setEditorKey(prev => prev + 1);
                  console.log("🔄 FORCED EDITOR KEY UPDATE after knowledge base regeneration");
                }, 100);

                // 🚨 ROBUST FOCUS FIX: Keep focus on the regenerated section
                console.log("🎯 Setting focus to regenerated section:", sectionId);
                setSelectedSectionId(sectionId);

                // Force focus with a slight delay to ensure state has updated
                setTimeout(() => {
                  console.log("🎯 Double-checking focus on section:", sectionId);
                  setSelectedSectionId(sectionId);
                }, 100);

                console.log("✅ Section content updated in UI and focus maintained");
              }
            } catch (error) {
              console.warn("Failed to parse SSE data:", error);
            }
          }
        }
      }

      toast({
        title: "Section Regenerated",
        description: `Successfully regenerated "${sectionTitle}" with knowledge base and template data.`,
      });

    } catch (error) {
      console.error("❌ Section regeneration with knowledge base failed:", error);
      toast({
        title: "Regeneration Failed",
        description: `Failed to regenerate section: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setRegenerationProgress(prev => ({
        ...prev,
        isGenerating: false,
      }));
    }
  };

  // 🚨 NEW: Handle section regeneration with requirements (like Regenerate All)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSectionRegenerationWithRequirements = async () => {
    if (!selectedSectionForRegeneration || !regenerationData) {
      console.error("❌ Missing required data for section regeneration:", {
        hasSelectedSection: !!selectedSectionForRegeneration,
        hasRegenerationData: !!regenerationData
      });
      toast({
        title: "Missing Data",
        description: "Required data for section regeneration is missing. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsRegeneratingSection(true);
    setShowSectionModeDialog(false);

    try {
      console.log("🔄 Starting section regeneration with requirements:", {
        section: selectedSectionForRegeneration.title,
        sectionId: selectedSectionForRegeneration.id,
        documentId: regenerationData.existingDocument?.id,
        hasNewClientData: !!regenerationData.newClientData,
        hasNewRequirements: !!regenerationData.newRequirements
      });

      // 🚨 VALIDATION: Ensure all required data exists
      if (!regenerationData.existingDocument?.id) {
        throw new Error("Existing document ID is missing from regeneration data");
      }

      if (!regenerationData.newClientData) {
        throw new Error("New client data is missing from regeneration data");
      }

      if (!regenerationData.newRequirements) {
        throw new Error("New requirements data is missing from regeneration data");
      }

      // 🚨 NEW: Find the actual section content and extract images
      const targetSection = editableSections.find(s => s.id === selectedSectionForRegeneration.id);
      if (!targetSection) {
        throw new Error(`Section with ID ${selectedSectionForRegeneration.id} not found in current sections`);
      }

      const originalImages = extractImagesFromContent(targetSection.content);
      console.log("🖼️ Found", originalImages.length, "images in section:", selectedSectionForRegeneration.title);

      // Show progress
      setRegenerationProgress({
        isGenerating: true,
        progress: 0,
        currentStep: `Regenerating section: ${selectedSectionForRegeneration.title}`,
        message: "Processing section with new requirements and preserving images",
        completedSections: [],
      });

      // Prepare payload for section regeneration (same structure as full regeneration)
      const sectionRegenerationPayload = {
        existingDocumentId: regenerationData.existingDocument.id,
        sectionId: selectedSectionForRegeneration.id,
        sectionTitle: selectedSectionForRegeneration.title,
        currentContent: targetSection.content, // 🚨 NEW: Include current content
        originalImages: originalImages, // 🚨 NEW: Include extracted images
        newClientData: regenerationData.newClientData,
        newRequirements: regenerationData.newRequirements,
        selectedKnowledgeDocuments: regenerationData.selectedKnowledgeDocuments || [],
        aiPrompts: regenerationData.aiPrompts || {},
        preserveImages: true, // 🚨 NEW: Flag to preserve images
        imageInstructions: "Do not generate any placeholder images, dummy images, or image references like 'Image not available', 'Mission Statement Image', 'Company Logo', etc. Only preserve existing real images.",
      };

      console.log("📋 SECTION REGENERATION PAYLOAD:", JSON.stringify(sectionRegenerationPayload, null, 2));

      // Call existing section regeneration API endpoint
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tender/regenerate/section/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "scopingai",
            "x-user-id": user?.id || "",
          },
          body: JSON.stringify(sectionRegenerationPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      // Handle Server-Sent Events for real-time progress
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                console.log("📡 Section regeneration SSE:", data);

                if (data.progress !== undefined) {
                  setRegenerationProgress((prev) => ({
                    ...prev,
                    progress: data.progress,
                    message: data.message || prev.message,
                    currentStep: data.step || prev.currentStep,
                  }));
                }

                // Process regenerated section result
                if (data.status === "completed" && data.result) {
                  console.log("🚨 SECTION REGENERATION COMPLETED:", data.result);

                  // 🚨 NEW: Get original images to preserve them
                  const targetSection = editableSections.find(s => s.id === selectedSectionForRegeneration.id);
                  const originalImages = targetSection ? extractImagesFromContent(targetSection.content) : [];

                  // 🚨 NEW: Merge images back into regenerated content
                  const regeneratedContentWithImages = mergeImagesIntoContent(
                    data.result.content || targetSection?.content || "",
                    originalImages
                  );

                  // Update the specific section in the array
                  const updatedSections = editableSections.map(section => {
                    if (section.id === selectedSectionForRegeneration.id) {
                      return {
                        ...section,
                        content: regeneratedContentWithImages,
                        regenerated: true,
                        regeneratedAt: new Date().toISOString(),
                      };
                    }
                    return section;
                  });

                  // Update UI with regenerated section
                  setEditableSections(updatedSections);

                  // Save to session storage for persistence
                  try {
                    const existingSessionData = sessionStorage.getItem("scopingai_proposal_data");
                    const sessionData = existingSessionData ? JSON.parse(existingSessionData) : {};

                    const updatedSessionData = {
                      ...sessionData,
                      regeneratedSections: updatedSections,
                      isRegenerated: true,
                      regeneratedAt: new Date().toISOString(),
                    };

                    sessionStorage.setItem("scopingai_proposal_data", JSON.stringify(updatedSessionData));
                    console.log("🚨 SECTION REGENERATED CONTENT SAVED TO SESSION STORAGE");
                  } catch (error) {
                    console.error("❌ Failed to save section regenerated content:", error);
                  }
                }

                if (data.error) {
                  throw new Error(data.error);
                }
              } catch (parseError) {
                console.warn("Failed to parse section regeneration SSE data:", parseError);
              }
            }
          }
        }
      }

      // 🚨 ROBUST FOCUS FIX: Keep focus on the regenerated section
      console.log("🎯 Setting focus to regenerated section:", selectedSectionForRegeneration.id);
      setSelectedSectionId(selectedSectionForRegeneration.id);

      // Force focus with a slight delay to ensure state has updated
      setTimeout(() => {
        console.log("🎯 Double-checking focus on section:", selectedSectionForRegeneration.id);
        setSelectedSectionId(selectedSectionForRegeneration.id);
      }, 100);

      toast({
        title: "Success!",
        description: `Section "${selectedSectionForRegeneration.title}" regenerated successfully with new requirements!`,
        duration: 5000,
      });

    } catch (error) {
      console.error("❌ Section regeneration with requirements failed:", error);
      toast({
        title: "Section Regeneration Failed",
        description: `Failed to regenerate section with requirements: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsRegeneratingSection(false);
      setSelectedSectionForRegeneration(null);
      setRegenerationProgress((prev) => ({
        ...prev,
        isGenerating: false,
      }));
    }
  };

  // 🚨 NEW: Helper function to get existing file path for updates
  const getExistingFilePath = async (documentId: string): Promise<string | null> => {
    try {
      const { data: documentRecord, error } = await supabase
        .from("scopingai_documents")
        .select("proposalFilePath")
        .eq("id", documentId)
        .single();

      if (error || !documentRecord) {
        console.warn("⚠️ Could not fetch existing file path:", error);
        return null;
      }

      return documentRecord.proposalFilePath || null;
    } catch (error) {
      console.error("❌ Error fetching existing file path:", error);
      return null;
    }
  };

  // Session storage cleanup function removed - not needed

  // 🚨 NEW: Prevent duplicate saves with request tracking
  const [saveRequestId, setSaveRequestId] = useState<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveTimeRef = useRef<number>(0);

  // 🚨 NEW: Debounced save function to prevent rapid multiple saves
  const debouncedSave = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastSave = now - lastSaveTimeRef.current;

    // Prevent saves within 2 seconds of each other
    if (timeSinceLastSave < 2000) {
      console.warn(`⚠️ Save blocked - only ${timeSinceLastSave}ms since last save (minimum 2000ms)`);
      return;
    }

    lastSaveTimeRef.current = now;
    await handleSaveDocument();
  }, []);

  // Handle save document (update existing or create new based on source)
  const handleSaveDocument = async () => {
    // 🚨 CRITICAL: Prevent duplicate saves
    if (isSaving) {
      console.warn("⚠️ Save already in progress, ignoring duplicate request");
      return;
    }

    // Generate unique request ID to prevent duplicates
    const requestId = `save_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    // Clear any pending save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    try {
      setIsSaving(true);
      setSaveRequestId(requestId);

      console.log(`🚨 SAVE REQUEST STARTED: ${requestId}`);
      console.log("🔍 Save function entry state:", {
        hasRegenerationData: !!regenerationData,
        isQuickProposal: regenerationData?.fromQuickProposal,
        initializationState: initializationState,
        sectionsCount: editableSectionsRef.current.length,
        requestId: requestId
      });

      console.log("🚨 CRITICAL DEBUG - Current regenerationData state:", {
        regenerationDataIsNull: regenerationData === null,
        regenerationDataIsUndefined: regenerationData === undefined,
        regenerationDataType: typeof regenerationData,
        regenerationDataKeys: regenerationData ? Object.keys(regenerationData) : "N/A"
      });

      // Small delay to ensure any pending content changes are processed
      await new Promise(resolve => setTimeout(resolve, 100));

      // 🚨 CRITICAL FIX: If regenerationData is null, create it from session storage
      let currentRegenerationData = regenerationData;

      if (!currentRegenerationData) {
        console.error("❌ regenerationData is null - attempting to create from session storage");

        try {
          const sessionData = sessionStorage.getItem("scopingai_proposal_data");
          if (!sessionData) {
            throw new Error("No session data available");
          }

          const parsedData = JSON.parse(sessionData);
          console.log("🔧 Creating regenerationData from session storage:", {
            fromProposalsPage: parsedData.fromProposalsPage,
            fromQuickProposal: parsedData.fromQuickProposal,
            documentId: parsedData.document?.id || parsedData.selectedDocument?.id,
            hasDocument: !!(parsedData.document || parsedData.selectedDocument),
            projectTitle: parsedData.project?.title,              // 🚨 Quick Proposal stores here
            proposalTitle: parsedData.proposal?.title,
            requirementsProjectTitle: parsedData.requirements?.projectTitle,
            directProjectTitle: parsedData.projectTitle,
            documentTitle: parsedData.document?.title,
            selectedDocumentTitle: parsedData.selectedDocument?.title,
            allSessionKeys: Object.keys(parsedData)
          });

          // Create minimal regeneration data for save operation
          const emergencyRegenerationData = {
            existingDocument: {
              id: parsedData.document?.id || parsedData.selectedDocument?.id,
              title: parsedData.document?.title || parsedData.selectedDocument?.title || "Document",
              content: parsedData.document?.content || parsedData.selectedDocument?.content || "",
              template_id: parsedData.document?.template_id || null,
              sections: [],
            },
            newClientData: {
              id: parsedData.client?.id || null,
              name: parsedData.client?.name || "Client",
              email: parsedData.client?.email || "",
              company: parsedData.client?.company || "Company",
              description: parsedData.client?.description || "",
            },
            newRequirements: {
              projectTitle: parsedData.project?.title ||           // 🚨 FIX: Quick Proposal stores title here
                           parsedData.proposal?.title ||
                           parsedData.requirements?.projectTitle ||
                           parsedData.projectTitle ||
                           parsedData.document?.title ||
                           parsedData.selectedDocument?.title ||
                           "Project",
              description: parsedData.project?.description || "",
              objectives: [],
              timeline: parsedData.project?.timeline || "",
              budget: parsedData.project?.budget || "",
            },
            selectedKnowledgeDocuments: [],
            documentRequirements: {},
            aiPrompts: {},
            fromProposalsPage: parsedData.fromProposalsPage === true,
            fromQuickProposal: parsedData.fromQuickProposal === true,
            regenerateOnly: false,
          };

          // Set the regeneration data immediately
          setRegenerationData(emergencyRegenerationData);

          // Use the emergency data for this save operation
          currentRegenerationData = emergencyRegenerationData;

          console.log("✅ Emergency regenerationData created successfully");

        } catch (error) {
          console.error("❌ Failed to create emergency regenerationData:", error);
          toast({
            title: "Save Error",
            description: "Cannot save - document data is missing. Please refresh the page and try again.",
            variant: "destructive",
          });
          return;
        }
      }

      const isUpdatingExisting = currentRegenerationData.fromProposalsPage === true;

      console.log("🔍 Save operation determination:", {
        fromProposalsPage: currentRegenerationData.fromProposalsPage,
        fromQuickProposal: currentRegenerationData.fromQuickProposal,
        isUpdatingExisting: isUpdatingExisting,
        operation: isUpdatingExisting ? "UPDATE_EXISTING" : "CREATE_NEW",
        documentId: currentRegenerationData.existingDocument?.id,
        documentTitle: currentRegenerationData.existingDocument?.title
      });

      console.log(isUpdatingExisting ? "💾 Updating existing proposal..." : "💾 Saving regenerated document as new proposal...");

      // 🚨 FIXED: Deduplicate sections before saving to prevent duplication
      const uniqueSections = editableSectionsRef.current.filter((section, index, array) => {
        const firstIndex = array.findIndex(s => s.id === section.id);
        return firstIndex === index;
      });

      // 🚨 FIXED: Filter out sections with no content to prevent empty sections
      const sectionsWithContent = uniqueSections.filter(section =>
        section.content && section.content.trim().length > 0
      );

      // 🚨 CRITICAL DEBUG: Log sections that were filtered out
      const sectionsWithoutContent = uniqueSections.filter(section =>
        !section.content || section.content.trim().length === 0
      );

      if (sectionsWithoutContent.length > 0) {
        console.log("⚠️ SECTIONS FILTERED OUT (no content):", sectionsWithoutContent.map(s => ({
          id: s.id,
          title: s.title,
          hasContent: !!s.content,
          contentLength: s.content?.length || 0,
          regenerated: s.regenerated,
          edited: s.edited
        })));
      }

      console.log("💾 Section deduplication and filtering:", {
        original: editableSectionsRef.current.length,
        afterDeduplication: uniqueSections.length,
        withContent: sectionsWithContent.length,
        duplicatesRemoved: editableSectionsRef.current.length - uniqueSections.length,
        emptyRemoved: uniqueSections.length - sectionsWithContent.length
      });

      // Update the ref to use cleaned sections
      editableSectionsRef.current = sectionsWithContent;

      // Log save operation
      console.log("💾 Saving document with", sectionsWithContent.length, "sections");
      console.log("💾 Regeneration data exists:", !!regenerationData);
      console.log("💾 Sections to save:", sectionsWithContent.map(s => ({ id: s.id, title: s.title, hasContent: !!s.content })));

      // Removed problematic fallback logic that was overriding fromProposalsPage flag

      // 🚨 FIXED: Check initialization state with special handling for quick proposal
      console.log("🔍 Initialization state check:", {
        canSave: initializationState.canSave,
        isInitializing: initializationState.isInitializing,
        hasError: initializationState.hasError,
        errorMessage: initializationState.errorMessage,
        hasRegenerationData: !!regenerationData,
        isQuickProposal: regenerationData?.fromQuickProposal,
        sectionsCount: sectionsWithContent.length
      });

      // 🚨 CRITICAL FIX: Enhanced logic for quick proposal, edited content, and regenerated content
      const isQuickProposalWithContent = currentRegenerationData?.fromQuickProposal && sectionsWithContent.length > 0;
      const hasEditedContent = sectionsWithContent.some(section => section.edited || section.content?.trim().length > 0);
      const hasRegeneratedContent = sectionsWithContent.some(section => section.regenerated && section.content?.trim().length > 0);

      // 🚨 QUICK PROPOSAL FIX: For Quick Proposal flow, allow save if there are any sections with content
      const isQuickProposalFlow = currentRegenerationData?.fromQuickProposal === true;
      const hasAnyContent = sectionsWithContent.length > 0 && sectionsWithContent.some(s => s.content?.trim().length > 0);

      const shouldAllowSave = isQuickProposalWithContent ||
                             hasEditedContent ||
                             hasRegeneratedContent ||  // 🚨 NEW: Allow save for regenerated content
                             initializationState.canSave ||
                             (isQuickProposalFlow && hasAnyContent);

      console.log("🔍 Save permission check:", {
        canSave: initializationState.canSave,
        isQuickProposal: currentRegenerationData?.fromQuickProposal,
        sectionsWithContent: sectionsWithContent.length,
        hasEditedContent: hasEditedContent,
        hasRegeneratedContent: hasRegeneratedContent,  // 🚨 NEW: Log regenerated content check
        isQuickProposalFlow: isQuickProposalFlow,
        hasAnyContent: hasAnyContent,
        shouldAllowSave: shouldAllowSave,
        initializationState: initializationState
      });

      if (!shouldAllowSave) {
        console.error("❌ Save not allowed - no valid content or initialization failed");

        // 🚨 QUICK PROPOSAL DEBUG: Extra logging for Quick Proposal flow
        if (isQuickProposalFlow) {
          console.error("🚨 QUICK PROPOSAL SAVE BLOCKED:", {
            sectionsWithContent: sectionsWithContent.length,
            sectionsData: sectionsWithContent.map(s => ({
              id: s.id,
              title: s.title,
              hasContent: !!s.content?.trim(),
              contentLength: s.content?.length || 0,
              edited: s.edited
            })),
            hasAnyContent: hasAnyContent,
            shouldAllowSave: shouldAllowSave
          });
        }

        toast({
          title: "Cannot Save Document",
          description: initializationState.hasError
            ? `Error: ${initializationState.errorMessage}. Please refresh the page.`
            : "No content to save or document is still loading. Please add content and try again.",
          variant: "destructive",
        });
        return;
      }

      // Force enable initialization state if we have valid content to save
      if (!initializationState.canSave && shouldAllowSave) {
        console.log("🚨 Forcing save enabled due to valid content");
        setInitializationState(prev => ({
          ...prev,
          canSave: true,
          isInitializing: false,
          hasError: false,
          errorMessage: ""
        }));
      }

      // 🚨 FIXED: Use the already cleaned sections (no need for duplicate validation)
      if (!editableSectionsRef.current.length) {
        throw new Error("No sections with content to save");
      }

      const hasSectionsToSave = sectionsWithContent.length > 0;

      if (!hasSectionsToSave) {
        throw new Error("No content found in sections. Please add some content before saving.");
      }

      // Import the save function
      const { saveDocument } = await import("../services/documentService");
      const { supabase } = await import("../../../foundation/altzero_base/base/utils/supabaseClient");

      // Get current user
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) {
        throw new Error("Not authenticated");
      }

      // 🚨 FIX: Prepare document data based on whether we're updating or creating new
      // 🚨 FIXED: Use empty sections array in documentData - sections will come from sectionsToSave parameter
      // 🚨 CRITICAL FIX: Handle null regenerationData with fallback values
      const clientName = currentRegenerationData?.newClientData?.company || "Client";
      const projectTitle = currentRegenerationData?.newRequirements?.projectTitle || "Quick Proposal";
      const existingTitle = currentRegenerationData?.existingDocument?.title || "Document";

      // 🚨 FIXED: Proper title logic for different flows
      let documentTitle: string;
      let documentType: string;

      if (isUpdatingExisting) {
        // Updating existing document - keep original title
        documentTitle = existingTitle;
        documentType = "Updated Proposal";
      } else if (currentRegenerationData?.fromQuickProposal) {
        // Quick Proposal flow - use project title only, no "(Regenerated)" suffix
        documentTitle = projectTitle;
        documentType = "Quick Proposal";
      } else {
        // Regular regeneration flow - use client + project with "(Regenerated)" suffix
        documentTitle = `${clientName} - ${projectTitle} (Regenerated)`;
        documentType = "Regenerated Proposal";
      }

      console.log("📝 Document title logic:", {
        isUpdatingExisting,
        fromQuickProposal: currentRegenerationData?.fromQuickProposal,
        clientName,
        projectTitle,
        existingTitle,
        finalTitle: documentTitle,
        finalType: documentType,
        regenerationDataSource: regenerationData ? "original" : "emergency",
        projectTitleSource: {
          fromNewRequirements: currentRegenerationData?.newRequirements?.projectTitle,
          fallbackUsed: projectTitle === "Quick Proposal"
        }
      });

      const documentData = {
        title: documentTitle,
        client: clientName,
        type: documentType,
        sections: [], // 🚨 FIXED: Empty array - sections come from sectionsToSave parameter to prevent duplication
        overview: isUpdatingExisting
          ? `Updated proposal for ${clientName}`
          : currentRegenerationData?.fromQuickProposal
            ? `Quick proposal: ${projectTitle}`
            : `Regenerated proposal for ${clientName}`,
        metadata: {
          originalDocumentId: currentRegenerationData?.existingDocument?.id || `quick-proposal-${Date.now()}`,
          [isUpdatingExisting ? 'updatedAt' : 'regeneratedAt']: new Date().toISOString(),
          clientData: currentRegenerationData?.newClientData || {},
          projectData: currentRegenerationData?.newRequirements || {},
          aiPrompts: currentRegenerationData?.aiPrompts || {},
          knowledgeDocuments: currentRegenerationData?.selectedKnowledgeDocuments || [],
          documentRequirements: currentRegenerationData?.documentRequirements || {},
          isUpdate: isUpdatingExisting,
        }
      };

      // Create document theme with complete structure
      const documentTheme = {
        primaryColor: "#2563eb",
        secondaryColor: "#64748b",
        headingColor: "#1e293b",
        subheadingColor: "#475569",
        textColor: "#334155",
        backgroundColor: "#ffffff",
        fontFamily: "Inter",
        fontSize: "14px",
        headingSize: "medium" as const,
        textSize: "medium" as const,
        showCoverPage: true,
        logo: null,
      };

      // 🚨 SIMPLIFIED: Get client ID from regeneration data
      let clientId = currentRegenerationData.newClientData.id;

      // Validate clientId - never use user ID as client ID
      if (clientId === userData.user.id) {
        clientId = null;
      }

      console.log("📋 Client ID for save operation:", {
        clientId,
        clientName: currentRegenerationData.newClientData.name,
        clientCompany: currentRegenerationData.newClientData.company
      });



      // 🚨 FIXED: Use current edited content from ref (already deduplicated and filtered)
      let sectionsToSave = JSON.parse(JSON.stringify(editableSectionsRef.current));

      const editedCount = sectionsToSave.filter((s: any) => s.edited).length;
      const regeneratedCount = sectionsToSave.filter((s: any) => s.regenerated).length;
      const sectionsWithContentCount = sectionsToSave.filter((s: any) => s.content && s.content.trim()).length;

      console.log('🔄 STEP 3 - About to save:', {
        totalSections: sectionsToSave.length,
        editedSections: editedCount,
        regeneratedSections: regeneratedCount,
        sectionsWithContent: sectionsWithContentCount,
        fromProposalsPage: currentRegenerationData?.fromProposalsPage,
        fromQuickProposal: currentRegenerationData?.fromQuickProposal
      });

      // 🚨 CRITICAL DEBUG: Log each section's status
      console.log('🔍 SECTION-BY-SECTION DEBUG:');
      sectionsToSave.forEach((section: any, index: number) => {
        console.log(`Section ${index + 1} (${section.id}):`, {
          title: section.title,
          hasContent: !!(section.content && section.content.trim()),
          contentLength: section.content?.length || 0,
          edited: section.edited,
          regenerated: section.regenerated,
          regeneratedAt: section.regeneratedAt,
          willBeSaved: !!(section.content && section.content.trim())
        });
      });

      // 🚨 CRITICAL DEBUG: Log each section's content status
      console.log('📋 Section content analysis:');
      sectionsToSave.forEach((section: any, index: number) => {
        console.log(`  Section ${index + 1} (${section.id}): "${section.title}"`, {
          hasContent: !!(section.content && section.content.trim()),
          contentLength: section.content?.length || 0,
          edited: section.edited,
          regenerated: section.regenerated,
          contentPreview: section.content ? section.content.substring(0, 50) + '...' : 'NO CONTENT'
        });
      });

      // Debug: Log actual content being saved
      sectionsToSave.forEach((section: any, index: number) => {
        if (section.edited) {
          console.log(`📝 EDITED Section ${index + 1} (${section.title}):`, {
          contentLength: section.content?.length || 0,
            contentPreview: section.content ? section.content.substring(0, 100) + '...' : 'NO CONTENT'
          });
        }
      });





      // 🚨 VALIDATION: Ensure we have content to save
      if (!sectionsToSave || sectionsToSave.length === 0) {
        throw new Error("No sections to save. Please regenerate content first.");
      }

      // 🚨 FIXED: No need to validate empty sections - already filtered out during deduplication

      // Show progress toast
      toast({
        title: isUpdatingExisting ? "Updating Proposal" : "Saving Document",
        description: `${isUpdatingExisting ? 'Updating' : 'Saving'} ${sectionsToSave.length} sections to bucket storage...`,
        duration: 3000,
      });



      // 🚨 FIXED: Get document ID and file path from regenerationData
      let documentDbId = null;
      let existingFilePath = null;

      if (isUpdatingExisting) {
        documentDbId = currentRegenerationData.existingDocument.id;
        existingFilePath = await getExistingFilePath(documentDbId);

        console.log("📄 Update operation - using existing document:", {
          documentDbId,
          existingFilePath,
          documentTitle: currentRegenerationData.existingDocument.title
        });
      }

      console.log("🚨 FINAL SAVE PARAMETERS:", {
        isUpdatingExisting,
        documentDbId,
        existingFilePath,
        operation: isUpdatingExisting ? "UPDATE_EXISTING_DOCUMENT" : "CREATE_NEW_DOCUMENT",
        documentTitle: documentData.title,
        sectionsCount: sectionsToSave.length,
        fromProposalsPage: currentRegenerationData?.fromProposalsPage,
        emergencyDataUsed: !regenerationData && !!currentRegenerationData
      });

      // 🚨 CRITICAL DEBUG: Log the actual saveDocument call parameters
      console.log("🚨 CALLING saveDocument with:", {
        documentData: {
          title: documentData.title,
          client: documentData.client,
          type: documentData.type
        },
        sectionsCount: sectionsToSave.length,
        documentDbId: documentDbId,
        clientId: clientId,
        existingFilePath: existingFilePath,
        isUpdate: !!documentDbId
      });

      // 🚨 FIX: Save or update document based on source
      const result = await saveDocument(
        documentData,
        sectionsToSave, // Use regenerated sections
        documentTheme,
        documentDbId, // Use safely extracted document ID
        clientId,
        null, // promptTemplateId
        null, // scopeTemplateId
        existingFilePath // Use safely extracted file path
      );

      console.log("✅ Document save result:", {
        result: result,
        documentId: result?.documentId,
        filePath: result?.filePath,
        wasUpdate: !!documentDbId,
        originalDocumentId: documentDbId,
        operation: isUpdatingExisting ? "UPDATE" : "CREATE"
      });

      // 🚨 CRITICAL: Verify the save actually worked
      if (!result || !result.documentId) {
        throw new Error("Save operation failed - no document ID returned");
      }

      // Session storage update removed - not needed for proper operation

      // Check if this is a new proposal from quick proposal
      const isNewProposal = currentRegenerationData?.existingDocument?.id?.startsWith('new-proposal-');
      
      // 🚨 FIXED: More specific success messages for different flows
      let successTitle: string;
      let successDescription: string;

      if (isUpdatingExisting) {
        successTitle = "✅ Proposal Updated Successfully!";
        successDescription = `Updated proposal with ${sectionsToSave.length} sections. You can continue editing or switch to preview.`;
      } else if (currentRegenerationData?.fromQuickProposal) {
        successTitle = "✅ Quick Proposal Created Successfully!";
        successDescription = `Created "${projectTitle}" with ${sectionsToSave.length} sections. You can continue editing or switch to preview.`;
      } else {
        successTitle = "✅ Document Saved Successfully!";
        successDescription = `Saved regenerated proposal with ${sectionsToSave.length} sections. You can continue editing or switch to preview.`;
      }

      toast({
        title: successTitle,
        description: successDescription,
        duration: 3000,
      });

      console.log(`✅ Document ${isUpdatingExisting ? 'updated' : 'saved'} successfully`);

      // 🚨 FIXED: Only redirect for "Save as New", not for "Update"
      if (!isUpdatingExisting) {
        console.log("🔄 Redirecting to proposals page after save (new proposal)");
        setTimeout(() => {
          navigate('/tender/proposals');
        }, 2000); // Give user time to see the success message
      } else {
        console.log("✅ Update completed - staying on current page");

        // 🚨 CRITICAL FIX: Mark proposals page data as stale so it refreshes when user goes back
        sessionStorage.setItem('scopingai_proposals_stale', 'true');
        console.log("🔄 Marked proposals page data as stale for refresh");
      }



      // 🚨 CRITICAL: Update the local editableSections state with the saved content
      // This ensures the preview shows the updated content immediately
      setEditableSections(prevSections => {
        const updatedSections = prevSections.map(section => {
          const savedSection = sectionsToSave.find(s => s.id === section.id);
          if (savedSection) {
            return {
              ...section,
              content: savedSection.content,
              edited: false, // Reset edited flag since it's now saved
              lastModified: new Date().toISOString()
            };
          }
          return section;
        });
        

        
        return updatedSections;
      });

      // Switch to preview mode to show the saved content
      setIsEditing(false);
      
      // Force preview update to ensure saved content is displayed
      setTimeout(() => {
        console.log('🔄 Forcing preview update after save');
        setPreviewUpdateTrigger(prev => prev + 1);
        // Force editor re-initialization for next edit session
        setEditorKey(prev => prev + 1);
      }, 200); // Increased delay to ensure state update has propagated
    } catch (error) {
      const isUpdatingExisting = regenerationData?.fromProposalsPage;
      console.error(`❌ Failed to ${isUpdatingExisting ? 'update' : 'save'} regenerated document:`, error);
      toast({
        title: "Error",
        description: `Failed to ${isUpdatingExisting ? 'update' : 'save'} document: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
      setSaveRequestId(null);
      console.log(`🚨 SAVE REQUEST COMPLETED: ${requestId}`);

      // Clear any pending save timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    }
  };

  // Auto-trigger regeneration if requested (ONLY for document generation flow, NOT proposals page or quick proposal)
  useEffect(() => {
    const shouldAutoTrigger = sessionStorage.getItem('autoTriggerRegeneration');

    // 🚨 FIX: Don't auto-trigger if coming from proposals page, quick proposal, or regenerateOnly flag
    if (regenerationData?.fromProposalsPage || 
        regenerationData?.existingDocument?.id?.startsWith('new-proposal-') ||
        regenerationData?.regenerateOnly === true) {
      console.log("🚨 SKIPPING AUTO-TRIGGER: Coming from proposals page, new proposal, or regenerateOnly flag - user should control regeneration");
      // Clean up auto-trigger flag if it exists
      if (shouldAutoTrigger === 'true') {
        sessionStorage.removeItem('autoTriggerRegeneration');
      }
      return;
    }

    if (shouldAutoTrigger === 'true' && regenerationData && !regenerationProgress.isGenerating && editableSectionsRef.current.length > 0) {
      console.log("🚀 Auto-triggering full regeneration after component initialization...");
      // Clear the flag to prevent multiple triggers
      sessionStorage.removeItem('autoTriggerRegeneration');

      // Show notification that auto-regeneration is starting
      toast({
        title: "Auto-Regeneration Started",
        description: "Regenerating your proposal with new requirements...",
        duration: 3000,
      });

      // Clean up URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('autoRegenerate');
      window.history.replaceState({}, '', url.toString());

      // Auto-trigger regeneration
      console.log("Auto-regenerating document with", editableSectionsRef.current.length, "sections");
      handleFullRegeneration();
    }
  }, [regenerationData, regenerationProgress.isGenerating, editableSectionsRef.current.length]);

  // Cleanup logic removed - not needed without session storage dependencies

  // Beautiful Regeneration Modal Component
  const RegenerationModal = () => {
    if (!showRegenerationModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 transform animate-in fade-in-0 zoom-in-95 duration-300">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="relative mx-auto w-16 h-16 mb-4">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-pulse"></div>
              <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                <Zap className="w-6 h-6 text-blue-600 animate-bounce" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Regenerating Proposal
            </h3>
            <p className="text-gray-600">
              AI is working on your proposal with new requirements...
            </p>
          </div>

          {/* Progress Section */}
          <div className="space-y-4">
            <div className="flex justify-between text-sm font-medium">
              <span className="text-gray-700">{regenerationProgress.currentStep}</span>
              <span className="text-blue-600">{regenerationProgress.progress}%</span>
            </div>

            {/* Animated Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-500 ease-out relative"
                style={{ width: `${regenerationProgress.progress}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              </div>
            </div>

            {/* Status Message */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <p className="text-blue-800 text-sm font-medium">
                  {regenerationProgress.message || "Processing your request..."}
                </p>
              </div>
            </div>

            {/* Completed Sections */}
            {regenerationProgress.completedSections.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Completed Sections:</p>
                <div className="space-y-1">
                  {regenerationProgress.completedSections.map((section, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span>{section}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              This may take a few minutes. Please don't close this window.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // 🚨 NEW: State for regeneration method popup
  const [showRegenerationMethodPopup, setShowRegenerationMethodPopup] = useState(false);
  const [pendingRegenerationType, setPendingRegenerationType] = useState<'full' | 'section' | null>(null);
  const [pendingSectionId, setPendingSectionId] = useState<string | null>(null);
  const [pendingSectionTitle, setPendingSectionTitle] = useState<string | null>(null);

  // 🚨 IMPROVED: Unified Regeneration Popup Component (Better UX - 1 click instead of 2)
  const RegenerationMethodPopup = () => {
    console.log('🔍 RegenerationMethodPopup render check:', {
      showRegenerationMethodPopup,
      pendingRegenerationType,
      pendingSectionId,
      pendingSectionTitle
    });

    if (!showRegenerationMethodPopup) return null;

    // 🎯 NEW: Detect if user came from "From Existing" flow
    const isFromExistingFlow = location.state?.fromExistingFlow === true;
    const isFromExistingProposal = !regenerationData?.fromProposalsPage && !regenerationData?.fromQuickProposal;
    const shouldShowKnowledgeBaseOption = (isFromExistingFlow || isFromExistingProposal) &&
                                         (pendingRegenerationType === 'full' || pendingRegenerationType === 'section');

    // 🚨 IMPROVED: Direct action handlers (no intermediate step)
    const handleKnowledgeBaseRegeneration = async () => {
      setShowRegenerationMethodPopup(false);

      if (pendingRegenerationType === 'full') {
        console.log('🔧 Starting knowledge base full regeneration directly');
        setShowKnowledgePromptDialog(true);
      } else if (pendingRegenerationType === 'section' && pendingSectionId && pendingSectionTitle) {
        console.log('🔧 Starting knowledge base section regeneration directly');
        setSelectedSectionForRegeneration({ id: pendingSectionId, title: pendingSectionTitle });
        setShowKnowledgePromptDialog(true);
      }

      // Reset pending state
      setPendingRegenerationType(null);
      setPendingSectionId(null);
      setPendingSectionTitle(null);
    };

    const handleAIPromptRegeneration = async () => {
      setShowRegenerationMethodPopup(false);

      if (pendingRegenerationType === 'full') {
        console.log('🔧 Starting AI prompt full regeneration directly');
        setShowPromptDialog(true);
      } else if (pendingRegenerationType === 'section' && pendingSectionId && pendingSectionTitle) {
        console.log('🔧 Starting AI prompt section regeneration directly');
        setSelectedSectionForRegeneration({ id: pendingSectionId, title: pendingSectionTitle });
        setShowSectionPromptDialog(true);
      }

      // Reset pending state
      setPendingRegenerationType(null);
      setPendingSectionId(null);
      setPendingSectionTitle(null);
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 transform animate-in fade-in-0 zoom-in-95 duration-300">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="relative mx-auto w-16 h-16 mb-4">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"></div>
              <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Regenerate {pendingRegenerationType === 'full' ? 'Proposal' : 'Section'}
            </h3>
            <p className="text-gray-600">
              Choose how you'd like to regenerate your content
            </p>
          </div>

          {/* 🚨 IMPROVED: Action-oriented options with clear benefits */}
          <div className="space-y-4 mb-8">
            {/* Knowledge Base Option - More prominent and action-oriented */}
            {shouldShowKnowledgeBaseOption && (
              <div
                onClick={handleKnowledgeBaseRegeneration}
                className="group cursor-pointer p-6 border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 hover:shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Use Knowledge Base & Templates</h4>
                    <p className="text-gray-600 text-sm mb-3">
                      Leverage your existing knowledge base documents and proposal templates for accurate, data-driven content
                    </p>
                    <div className="flex items-center text-blue-600 text-sm font-medium">
                      <span>Get started</span>
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AI Prompt Option - More prominent and action-oriented */}
            <div
              onClick={handleAIPromptRegeneration}
              className="group cursor-pointer p-6 border-2 border-purple-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all duration-200 hover:shadow-lg"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <Wand2 className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Use Custom AI Prompts</h4>
                  <p className="text-gray-600 text-sm mb-3">
                    Provide specific instructions to AI for precise content generation and customization
                  </p>
                  <div className="flex items-center text-purple-600 text-sm font-medium">
                    <span>Get started</span>
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cancel Button */}
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={() => {
                setShowRegenerationMethodPopup(false);
                setPendingRegenerationType(null);
                setPendingSectionId(null);
                setPendingSectionTitle(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    const shouldAutoTrigger = sessionStorage.getItem('autoTriggerRegeneration') === 'true';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <span className="text-gray-600">
            {shouldAutoTrigger
              ? "Preparing document for regeneration..."
              : "Loading document for regeneration..."
            }
          </span>
          {shouldAutoTrigger && (
            <p className="text-sm text-blue-600 mt-2">
              Regeneration will start automatically once ready
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!regenerationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center py-12">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium mb-2">Document Not Found</h3>
          <p className="text-gray-600 mb-4">
            The document you're trying to regenerate could not be loaded.
          </p>
          <Link to="/tender/documents">
            <Button variant="outline">Back to Documents</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <ScopingAILayout>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="flex-shrink-0 border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // 🚨 FIXED: Check where user came from and navigate back appropriately
                  const fromProposalsPage = regenerationData?.fromProposalsPage;
                  const fromQuickProposal = regenerationData?.fromQuickProposal;

                  console.log('🔄 Back button clicked:', {
                    fromProposalsPage,
                    fromQuickProposal,
                    regenerationData: regenerationData,
                    referrer: document.referrer
                  });

                  if (fromProposalsPage) {
                    console.log('🔄 Navigating back to proposals page');
                    navigate('/tender/proposals');
                  } else if (fromQuickProposal) {
                    console.log('🔄 Navigating back to quick proposal');
                    navigate('/tender/quick-proposal');
                  } else {
                    // 🚨 FALLBACK: Check referrer as backup
                    const referrer = document.referrer;
                    if (referrer.includes('/proposals')) {
                      console.log('🔄 Fallback: Navigating back to proposals page (from referrer)');
                      navigate('/tender/proposals');
                    } else if (referrer.includes('/quick-proposal')) {
                      console.log('🔄 Fallback: Navigating back to quick proposal (from referrer)');
                      navigate('/tender/quick-proposal');
                    } else {
                      console.log('🔄 Navigating back to documents page (default)');
                      navigate('/tender/documents');
                    }
                  }
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="group">
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  Document Regeneration
                  {regenerationProgress.isGenerating && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Regenerating
                    </span>
                  )}
                </h1>
                <div className="mt-1 space-y-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    Regenerating: 
                    {isEditingProjectTitle ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={projectTitleInput}
                          onChange={(e) => handleProjectTitleChange(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleProjectTitleSave();
                            } else if (e.key === "Escape") {
                              handleProjectTitleCancel();
                            }
                          }}
                          className="h-6 text-sm w-48"
                          autoFocus
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleProjectTitleSave}
                          className="h-6 px-2"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleProjectTitleCancel}
                          className="h-6 px-2"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{regenerationData.existingDocument.title}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={toggleProjectTitleEditing}
                          className="h-6 px-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Edit3 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </p>
                  {editableSections.length > 0 && (
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-gray-500">
                        Sections: {editableSections.length}
                      </span>
                      <span className="text-green-600">
                        Regenerated: {(() => {
                          const regeneratedCount = editableSections.filter(s => s.regenerated).length;
                          console.log("🚨 UI RENDER: editableSections.length:", editableSections.length);
                          console.log("🚨 UI RENDER: regeneratedCount:", regeneratedCount);
                          console.log("🚨 UI RENDER: sections with regenerated flag:", editableSections.filter(s => s.regenerated).map(s => s.title));
                          return regeneratedCount;
                        })()}
                      </span>
                      {editableSections.some(s => s.regenerated) && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ New Content Available
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">

              <Button
                variant="outline"
                size="sm"
                disabled={regenerationProgress.isGenerating || isRegeneratingWithPrompt}
                onClick={() => {
                  console.log('🔄 Regenerate All clicked:', {
                    regenerationData: regenerationData,
                    regenerateOnly: regenerationData?.regenerateOnly,
                    fromProposalsPage: regenerationData?.fromProposalsPage,
                    fromQuickProposal: regenerationData?.fromQuickProposal,
                    showRegenerationMethodPopup: showRegenerationMethodPopup
                  });


                  if (regenerationData?.fromQuickProposal === true) {
                    console.log('📋 Quick Proposal flow detected - Directly showing AI prompt dialog');
                    // For Quick Proposal flow, directly show AI prompt dialog (no knowledge base option)
                    setShowPromptDialog(true);
                  } else {
                    console.log('📋 Other flows detected - Showing regeneration method popup');
                    // Show popup with both options (Knowledge Base & Templates vs AI Prompt) for other flows
                    setPendingRegenerationType('full');
                    setShowRegenerationMethodPopup(true);
                  }
                  console.log('📋 Regenerate All logic completed');
                }}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${(regenerationProgress.isGenerating || isRegeneratingWithPrompt) ? 'animate-spin' : ''}`} />
                {regenerationProgress.isGenerating || isRegeneratingWithPrompt ? 'Regenerating...' : 'Regenerate All'}
              </Button>
              <Button
                disabled={isSaving || saveRequestId !== null || !regenerationData}
                onClick={(e) => {
                  // Prevent default and stop propagation to avoid multiple triggers
                  e.preventDefault();
                  e.stopPropagation();

                  // Ensure regenerationData exists before saving
                  if (!regenerationData) {
                    console.error("❌ Cannot save: regenerationData is null");
                    toast({
                      title: "Cannot Save",
                      description: "Document data is not ready. Please wait for initialization to complete.",
                      variant: "destructive",
                    });
                    return;
                  }

                  // Use debounced save to prevent duplicates
                  debouncedSave();
                }}
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {regenerationData?.fromProposalsPage === true ? "Update Proposal" : "Save as New Proposal"}
              </Button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {regenerationProgress.isGenerating && (
          <div className="flex-shrink-0 border-b bg-white px-6 py-3">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{regenerationProgress.message}</span>
                <span>{regenerationProgress.progress}%</span>
              </div>
              <Progress value={regenerationProgress.progress} className="h-2" />
            </div>
          </div>
        )}

        {/* Two-Column Layout */}
        <div className="flex flex-1 overflow-hidden bg-gray-50">

          {/* Column 1: Section List */}
          <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
            <div className="flex-shrink-0 p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Document Sections</h3>
              <p className="text-sm text-gray-600">
                {editableSections.length} sections • {editableSections.filter(s => s.edited).length} edited
              </p>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="p-2">
                {editableSections.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={editableSections.map((section) => section.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {editableSections.map((section) => (
                        <SortableSection
                          key={section.id}
                          section={section}
                          selectedSectionId={selectedSectionId}
                          editingTitles={editingTitles}
                          titleInputs={titleInputs}
                          onSectionClick={(sectionId) => {
                            setSelectedSectionId(sectionId);
                            // If currently on document preview, scroll to the selected section instead of switching tabs
                            if (activeTab === 'document-preview' && pdfPreviewRef.current) {
                              pdfPreviewRef.current.scrollToSection(sectionId);
                            }
                          }}
                          onTitleChange={handleTitleChange}
                          onTitleSave={handleTitleSave}
                          onTitleCancel={handleTitleCancel}
                          onEditClick={(sectionId) => {
                            setSelectedSectionId(sectionId);
                            setActiveTab('edit');
                            setIsEditing(true);
                          }}
                          onToggleTitleEditing={toggleTitleEditing}
                          onAddSectionAfter={handleAddSectionAfter}
                          onSectionRegenerate={(sectionId, sectionTitle) => {
                            // 🚨 CONDITIONAL: Only show knowledge base popup for "From Existing Proposal" flow
                            const isFromExistingProposal = !regenerationData?.fromProposalsPage && !regenerationData?.fromQuickProposal;

                            if (isFromExistingProposal) {
                              console.log('📋 From Existing Proposal flow - Showing regeneration method popup for section regeneration');
                              // Show popup with both options (Knowledge Base & Templates vs AI Prompt) for From Existing Proposal flow
                              setPendingRegenerationType('section');
                              setPendingSectionId(sectionId);
                              setPendingSectionTitle(sectionTitle);
                              setShowRegenerationMethodPopup(true);
                              console.log('📋 Set pendingRegenerationType to section and showRegenerationMethodPopup to true');
                            } else {
                              console.log('📋 Quick Proposal or Proposals Page flow - Directly showing AI prompt dialog');
                              // For Quick Proposal and Proposals Page flows, directly show AI prompt dialog
                              setSelectedSectionForRegeneration({ id: sectionId, title: sectionTitle });
                              setShowSectionPromptDialog(true);
                            }
                          }}
                          onDeleteSection={(sectionId) => {
                            const section = editableSections.find(s => s.id === sectionId);
                            if (editableSections.length <= 1) {
                              toast({
                                title: "Cannot Delete",
                                description: "Cannot delete the only section. At least one section must remain.",
                                variant: "destructive",
                              });
                              return;
                            }
                            if (confirm(`Are you sure you want to delete "${section?.title}"?`)) {
                              handleDeleteSection(sectionId);
                              toast({
                                title: "Section Deleted",
                                description: `"${section?.title}" has been removed.`,
                              });
                            }
                          }}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No sections available</p>
                  </div>
                )}
                
                {/* Add Section Button */}
                <div className="p-2 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={handleAddSection}
                  >
                    <Plus className="h-4 w-4" />
                    Add Section
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: Editor with Tabs */}
          <div className="flex-1 bg-white border-r border-gray-200 flex flex-col">
            {/* Tab Navigation */}
            <div className="flex-shrink-0 border-b border-gray-200">
              <div className="flex items-center justify-between px-4 py-2">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      setActiveTab('edit');
                      setIsEditing(true);
                    }}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'edit'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('section-preview');
                      setIsEditing(false);
                    }}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'section-preview'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Section Preview
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('document-preview');
                      setIsEditing(false);
                    }}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'document-preview'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Document Preview
                  </button>
                </div>
                {/* Tab-specific info */}
                <div className="text-sm text-gray-600">
                  {activeTab === 'edit' && selectedSection && (
                    <span>Editing: {selectedSection.title}</span>
                  )}
                  {activeTab === 'section-preview' && selectedSection && (
                    <span>Previewing: {selectedSection.title}</span>
                  )}
                  {activeTab === 'document-preview' && (
                    <span>{editableSections.length} sections</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons Bar */}
            <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {selectedSection && selectedSection.edited && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Modified
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Tab-specific action buttons */}
                  {activeTab === 'section-preview' && selectedSection && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        console.log('🔄 Regenerate Section clicked:', {
                          selectedSection: selectedSection,
                          regenerationData: regenerationData,
                          regenerateOnly: regenerationData?.regenerateOnly,
                          fromProposalsPage: regenerationData?.fromProposalsPage,
                          fromQuickProposal: regenerationData?.fromQuickProposal,
                          showRegenerationMethodPopup: showRegenerationMethodPopup
                        });

                        if (selectedSection) {
                          // 🚨 CONDITIONAL: Only show knowledge base popup for "From Existing Proposal" flow
                          const isFromExistingProposal = !regenerationData?.fromProposalsPage && !regenerationData?.fromQuickProposal;

                          if (isFromExistingProposal) {
                            console.log('📋 From Existing Proposal flow - Showing regeneration method popup for section regeneration');
                            // Show popup with both options (Knowledge Base & Templates vs AI Prompt) for From Existing Proposal flow
                            setPendingRegenerationType('section');
                            setPendingSectionId(selectedSection.id);
                            setPendingSectionTitle(selectedSection.title);
                            setShowRegenerationMethodPopup(true);
                            console.log('📋 Set pendingRegenerationType to section and showRegenerationMethodPopup to true');
                          } else {
                            console.log('📋 Quick Proposal or Proposals Page flow - Directly showing AI prompt dialog');
                            // For Quick Proposal and Proposals Page flows, directly show AI prompt dialog
                            setSelectedSectionForRegeneration({ id: selectedSection.id, title: selectedSection.title });
                            setShowSectionPromptDialog(true);
                          }
                        }
                      }}
                      className="gap-2"
                      disabled={isRegeneratingSection}
                    >
                      <Wand2 className={`h-4 w-4 ${isRegeneratingSection ? 'animate-spin' : ''}`} />
                      Regenerate Section
                    </Button>
                  )}

                  {activeTab === 'edit' && selectedSection && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          console.log('🔄 Regenerate Section clicked from Edit tab:', {
                            selectedSection: selectedSection,
                            regenerationData: regenerationData,
                            regenerateOnly: regenerationData?.regenerateOnly,
                            fromProposalsPage: regenerationData?.fromProposalsPage,
                            fromQuickProposal: regenerationData?.fromQuickProposal,
                            showRegenerationMethodPopup: showRegenerationMethodPopup
                          });

                          // 🚨 CONDITIONAL: Only show knowledge base popup for "From Existing Proposal" flow
                          const isFromExistingProposal = !regenerationData?.fromProposalsPage && !regenerationData?.fromQuickProposal;

                          if (isFromExistingProposal) {
                            console.log('📋 From Existing Proposal flow - Showing regeneration method popup for section regeneration');
                            // Show popup with both options (Knowledge Base & Templates vs AI Prompt) for From Existing Proposal flow
                            setPendingRegenerationType('section');
                            setPendingSectionId(selectedSection.id);
                            setPendingSectionTitle(selectedSection.title);
                            setShowRegenerationMethodPopup(true);
                            console.log('📋 Set pendingRegenerationType to section and showRegenerationMethodPopup to true');
                          } else {
                            console.log('📋 Quick Proposal or Proposals Page flow - Directly showing AI prompt dialog');
                            // For Quick Proposal and Proposals Page flows, directly show AI prompt dialog
                            setSelectedSectionForRegeneration({ id: selectedSection.id, title: selectedSection.title });
                            setShowSectionPromptDialog(true);
                          }
                        }}
                        disabled={isRegeneratingSection}
                        className="mr-2"
                      >
                        <Wand2 className={`h-4 w-4 mr-2 ${isRegeneratingSection ? 'animate-spin' : ''}`} />
                        Regenerate Section
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={(e) => {
                          // Prevent default and stop propagation to avoid multiple triggers
                          e.preventDefault();
                          e.stopPropagation();

                          // Use debounced save to prevent duplicates
                          debouncedSave();
                        }}
                        disabled={isSaving || saveRequestId !== null}
                        className="bg-blue-600 hover:bg-blue-700"
                        title="Save changes (Ctrl+S)"
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Save Changes
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Edit Tab */}
              {activeTab === 'edit' && selectedSection && (
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex-1 overflow-hidden">
                    <YooptaMarkdownEditor
                      key={`editor-${selectedSection.id}-${activeTab}-${editorKey}`}
                      initialValue={undefined}
                      markdownContent={(() => {
                        // 🚨 CRITICAL FIX: Always get the latest content from editableSections state
                        const currentSection = editableSections.find(s => s.id === selectedSection.id);
                        const content = currentSection?.content || "";

                        console.log("🔍 FORCE EDITOR REFRESH - Content loading:", {
                          sectionId: selectedSection.id,
                          editorKey: editorKey,
                          timestamp: Date.now(),
                          regenerated: currentSection?.regenerated,
                          regeneratedAt: currentSection?.regeneratedAt,
                          contentLength: content.length,
                          contentPreview: content.substring(0, 100) + (content.length > 100 ? '...' : ''),
                          fullContent: content
                        });

                        return content;
                      })()}
                      onChange={(markdown) => {
                        console.log('🚨 YooptaMarkdownEditor onChange RECEIVED in DocumentsRegeneration:', {
                          sectionId: selectedSection.id,
                          contentLength: markdown?.length || 0,
                          hasContent: !!(markdown && markdown.trim()),
                          preview: markdown ? markdown.substring(0, 50) + '...' : 'NO CONTENT'
                        });
                        handleSectionContentChange(selectedSection.id, markdown);
                      }}
                      placeholder={`Edit ${selectedSection.title} content...`}
                      className="h-full w-full overflow-auto"
                      autoFocus={true}
                      userId={user?.id}
                    />
                  </div>
                </div>
              )}

              {/* Section Preview Tab */}
              {activeTab === 'section-preview' && selectedSection && (
                <div
                  key={`preview-${selectedSection.id}-${previewUpdateTrigger}`}
                  className="flex-1 overflow-y-auto p-6"
                >
                  <div className="prose prose-sm max-w-none">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900">
                      {selectedSection.title}
                    </h2>
                    <div className="mb-2 text-xs text-gray-500">
                      {selectedSection.edited && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                          ✓ Modified
                        </span>
                      )}
                      Content length: {selectedSection.content?.length || 0} characters
                      {selectedSection.lastModified && (
                        <span className="ml-2 text-gray-400">
                          Last modified: {new Date(selectedSection.lastModified).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                    <PerfectMarkdownRenderer
                      content={selectedSection.content || 'No content available'}
                      className="prose-sm"
                    />
                  </div>
                </div>
              )}

              {/* Document Preview Tab */}
              {activeTab === 'document-preview' && (
                <div className="flex-1 overflow-hidden">
                  <PDFLikePreview
                    ref={pdfPreviewRef}
                    sections={editableSections}
                    documentTitle={regenerationData?.existingDocument?.title || 'Document Preview'}
                    clientData={{
                      company: regenerationData?.newClientData?.company || 'Client Company',
                      name: regenerationData?.newClientData?.name,
                      email: regenerationData?.newClientData?.email,
                    }}
                    projectData={{
                      title: regenerationData?.newRequirements?.projectTitle || 'Project Title',
                      description: regenerationData?.newRequirements?.description,
                    }}
                    onSectionClick={(sectionId) => {
                      setSelectedSectionId(sectionId);
                      setActiveTab('section-preview');
                    }}
                    selectedSectionId={selectedSectionId}
                    showSidebar={false}

                  />
                </div>
              )}

              {/* No Section Selected State */}
              {!selectedSection && activeTab !== 'document-preview' && (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Edit3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium mb-2">Select a Section</p>
                    <p className="text-sm">Choose a section from the left panel to start {activeTab === 'edit' ? 'editing' : 'previewing'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          </div>
        </div>

        {/* Knowledge Base + Custom Prompt Dialog */}
        {showKnowledgePromptDialog && (
          <Dialog open={showKnowledgePromptDialog} onOpenChange={setShowKnowledgePromptDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Knowledge Base + Custom Instructions</DialogTitle>
                <DialogDescription>
                  Provide specific instructions for how to use the knowledge base data in regeneration.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Custom Instructions</label>
                  <textarea
                    value={knowledgeCustomPrompt}
                    onChange={(e) => setKnowledgeCustomPrompt(e.target.value)}
                    placeholder="Example: Replace only placeholder values <> with knowledge base client data, keep everything else exactly the same..."
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                  />
                </div>
                <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-md">
                  <strong>💡 Tip:</strong> Be specific about what you want to change. The AI will follow your instructions exactly while using knowledge base data.
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowKnowledgePromptDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleKnowledgeWithPromptRegeneration} disabled={!knowledgeCustomPrompt.trim()}>
                    Regenerate with Knowledge Base
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Regeneration Prompt Dialog */}
        {showPromptDialog && (
          <Dialog open={showPromptDialog} onOpenChange={setShowPromptDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Regenerate All Sections</DialogTitle>
                <DialogDescription>
                  Provide custom instructions for regenerating all sections of this proposal.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Custom Instructions</label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Describe how you'd like the content to be regenerated..."
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPromptDialog(false);
                    setCustomPrompt("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowPromptDialog(false);
                    handleRegenerateWithPrompt();
                  }}
                  disabled={!customPrompt.trim()}
                  className="gap-2"
                >
                  <Wand2 className="h-4 w-4" />
                  Regenerate All
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Section Regeneration Prompt Dialog */}
        {showSectionPromptDialog && selectedSectionForRegeneration && (
          <Dialog open={showSectionPromptDialog} onOpenChange={setShowSectionPromptDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Regenerate Section</DialogTitle>
                <DialogDescription>
                  Regenerate "{selectedSectionForRegeneration.title}" with custom instructions.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Custom Instructions</label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Describe how you'd like this section to be regenerated..."
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSectionPromptDialog(false);
                    setCustomPrompt("");
                    setSelectedSectionForRegeneration(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowSectionPromptDialog(false);
                    handleRegenerateSectionWithPrompt();
                  }}
                  disabled={!customPrompt.trim()}
                  className="gap-2"
                >
                  <Wand2 className="h-4 w-4" />
                  Regenerate Section
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Regeneration Progress Modal */}
        {showRegenerationModal && (
          <Dialog open={showRegenerationModal} onOpenChange={() => {}}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Regenerating Proposal</DialogTitle>
                <DialogDescription>
                  {regenerationProgress.message}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">
                    {regenerationProgress.currentStep}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span>{regenerationProgress.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${regenerationProgress.progress}%` }}
                    />
                  </div>
                </div>
                {regenerationProgress.completedSections.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Completed Sections:</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {regenerationProgress.completedSections.map((section, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-green-700">
                          <CheckCircle className="w-4 h-4" />
                          <span>{section}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Regeneration Method Popup */}
        <RegenerationMethodPopup />
    </ScopingAILayout>
  );
}

// Main component (standalone implementation)
const DocumentsRegeneration: React.FC = () => {
  return <DocumentRegenerationContent />;
};

export default DocumentsRegeneration;
